import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Button as AntButton, Drawer, Input, Select, Spin, Table, Modal, Switch, Tooltip, Tabs } from 'antd';
import type { DrawerProps, TableColumnsType } from 'antd';
import { SearchOutlined, SettingOutlined, VerticalAlignMiddleOutlined } from '@ant-design/icons';
import { Box, Button, Chip, Divider, Pagination, Typography } from '@mui/material';
import { ProductCompanyLocation } from '../../../types';
import { fetchProductCompanyLocations } from '../../../services/api';
import { formatCurrency, formatNumber, formatExactCurrency, formatExactNumber, handleEntityLinkClick } from '../utils';

const { Search } = Input;

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 / page' },
  { value: 25, label: '25 / page' },
  { value: 50, label: '50 / page' },
];

interface ColumnSettingsModalProps {
  isVisible: boolean;
  onCancel: () => void;
  columns: Record<string, boolean>;
  columnOrder: Record<string, number>;
  onToggle: (key: string) => void;
  onReset: () => void;
  onReorder: (updatedOrder: Record<string, number>) => void;
  requiredColumns?: string[];
  columnDisplayNames: Record<string, string>;
}

const ColumnSettingsModal = React.memo(({
  isVisible,
  onCancel,
  columns,
  columnOrder,
  onToggle,
  onReset,
  onReorder,
  requiredColumns = [],
  columnDisplayNames,
}: ColumnSettingsModalProps) => {
  const [draggedItem, setDraggedItem] = React.useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = React.useState<string | null>(null);

  const sortedColumns = Object.entries(columns)
    .sort(([a], [b]) => {
      const orderA = columnOrder[a] || 999;
      const orderB = columnOrder[b] || 999;
      return orderA - orderB;
    });

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    setDraggedItem(key);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, key: string) => {
    e.preventDefault();
    setDragOverItem(key);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggedItem && dragOverItem && draggedItem !== dragOverItem) {
      const newOrder = { ...columnOrder };
      
      const allColumns = Object.entries(newOrder).sort(([, a], [, b]) => a - b);
      const draggedIndex = allColumns.findIndex(([key]) => key === draggedItem);
      const dropIndex = allColumns.findIndex(([key]) => key === dragOverItem);

      if (draggedIndex === -1 || dropIndex === -1) {
        return;
      }

      const reorderedColumns = [...allColumns];
      const [draggedColumn] = reorderedColumns.splice(draggedIndex, 1);
      reorderedColumns.splice(dropIndex, 0, draggedColumn);

      const updatedOrder = reorderedColumns.reduce((acc, [key], index) => {
        acc[key] = index + 1;
        return acc;
      }, {} as { [key: string]: number });

      onReorder(updatedOrder);
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <Modal
      title="Column Settings"
      open={isVisible}
      onCancel={onCancel}
      footer={[
        <AntButton key="reset" onClick={onReset}>
          Reset to Default
        </AntButton>,
        <AntButton key="close" type="primary" onClick={onCancel}>
          Close
        </AntButton>
      ]}
      bodyStyle={{ 
        maxHeight: '60vh',
        overflow: 'hidden',
        padding: 0 
      }}
      width={600}
    > 
      <div 
        className="pe-2 py-4 column-settings-modal-content" 
        style={{ 
          maxHeight: 'calc(60vh - 40px)',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}
      >
        <div className="mb-4 text-sm text-gray-600 px-4">
          Drag and drop to reorder columns. Toggle visibility with the switches.
        </div>
        <div className="space-y-2 px-4">
          {sortedColumns.map(([key, value]) => (
            <div
              key={key}
              className={clsx(
                'column-settings-item flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50',
                dragOverItem === key && 'drag-over',
                draggedItem === key && 'dragging'
              )}
              draggable={true}
              onDragStart={(e) => handleDragStart(e, key)}
              onDragEnter={(e) => handleDragEnter(e, key)}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            >
              <div className="flex items-center gap-3 cursor-move">
                <VerticalAlignMiddleOutlined className="text-primary move-icon" />
                <span className="font-medium capitalize">
                  {columnDisplayNames[key] || key.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={requiredColumns.includes(key) ? true : value}
                  onChange={() => onToggle(key)}
                  size="small"
                  disabled={requiredColumns.includes(key)}
                  className={clsx('column-settings-switch', value && 'bg-blue-500')}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
});

const DEFAULT_DRAWER_WIDTH = 1350;

const fallbackProductName = (value?: string): string => {
  const trimmed = (value || '').trim();
  return trimmed.length > 0 ? trimmed : 'Unnamed Product';
};

const safeNumber = (value: unknown): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const sanitizeUnit = (value?: string | null): string | undefined => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const resolveUnitLabel = (units: Set<string>): string | undefined => {
  if (units.size === 0) {
    return undefined;
  }
  return Array.from(units).join(', ');
};

const formatQuantityWithUnit = (quantity: number, unit?: string): string => {
  const formattedQuantity = formatNumber(quantity);
  return unit ? `${formattedQuantity} ${unit}` : formattedQuantity;
};

const formatQuantityTooltip = (quantity: number, unit?: string): string => {
  const baseQuantity = formatExactNumber(quantity);
  return unit ? `${baseQuantity} ${unit}` : baseQuantity;
};

const parseYear = (value?: string): number | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

const formatYearRange = (start?: string, end?: string): string => {
  const startYear = parseYear(start);
  const endYear = parseYear(end);
  if (startYear && endYear) {
    return startYear === endYear ? String(startYear) : `${startYear}-${endYear}`;
  }
  if (startYear) {
    return String(startYear);
  }
  if (endYear) {
    return String(endYear);
  }
  return '';
};

const computeCompanyKey = (location: ProductCompanyLocation): string => {
  if (location.userid) {
    return String(location.userid);
  }
  return location.company || `${location.latitude},${location.longitude}`;
};

const buildCityKey = (city?: string, state?: string, country?: string): string => {
  const normalize = (value?: string) => (value || '').trim().toLowerCase();
  return `${normalize(city)}|${normalize(state)}|${normalize(country)}`;
};

const buildCompanyLocationKey = (company?: string, city?: string, state?: string, country?: string): string => {
  const normalize = (value?: string) => (value || '').trim().toLowerCase();
  return [company, city, state, country].map(normalize).join('|');
};

export interface ProductAggregate {
  productName: string;
  totalQuantity: number;
  totalAmount: number;
  target: number;
  companyCount: number;
  averageRate: number;
  cityCount: number;
  unit?: string;
}

export interface CityProductInsight {
  cityKey: string;
  city: string;
  state: string;
  country: string;
  topProductName: string;
  topProductUnit?: string;
  totalQuantity: number;
  totalAmount: number;
  companyCount: number;
  productCount: number;
  products: CityProductRow[];
  companies: CityCompanyRow[];
}

type ProductAggregateInternal = {
  quantity: number;
  amount: number;
  target: number;
  companyIds: Set<string>;
  cityIds: Set<string>;
  units: Set<string>;
};

type CityProductInternal = {
  quantity: number;
  amount: number;
  companyIds: Set<string>;
  units: Set<string>;
};

type CityCompanyInternal = {
  companyKey: string;
  company: string;
  userid?: string;
  totalQuantity: number;
  totalAmount: number;
  productNames: Set<string>;
};

export interface CityProductRow {
  productName: string;
  totalQuantity: number;
  totalAmount: number;
  companyCount: number;
  averageRate: number;
  unit?: string;
}

export interface CityCompanyRow {
  companyKey?: string;
  company: string;
  userid?: string;
  city: string;
  state: string;
  country: string;
  totalQuantity: number;
  totalAmount: number;
  uniqueProducts: number;
}

export interface ProductInsightRow {
  productName: string;
  totalQuantity: number;
  totalAmount: number;
  target: number;
  targetYear?: string;
  averageRate: number;
  companyCount: number;
  cityCount: number;
  unit?: string;
}

export interface ProductCityRow {
  city: string;
  state: string;
  country: string;
  totalQuantity: number;
  totalAmount: number;
  companyCount: number;
  unit?: string;
}

interface CityProductsDrawerContext {
  city: string;
  state: string;
  country: string;
  rows: CityProductRow[];
}

interface ProductCitiesDrawerContext {
  productName: string;
  rows: ProductCityRow[];
  locations: ProductCompanyLocation[];
}

export const buildProductAggregates = (locations: ProductCompanyLocation[]): ProductAggregate[] => {
  const aggregateMap = new Map<string, ProductAggregateInternal>();

  locations.forEach((location) => {
    const companyKey = computeCompanyKey(location);
    const cityKey = buildCityKey(location.city, location.state, location.country);
    location.products.forEach((product) => {
      const productName = fallbackProductName(product.product_name);
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      const amount = quantity * rate;
      const unit = sanitizeUnit(product.unit);
      const target = safeNumber(product.target);

      if (!aggregateMap.has(productName)) {
        aggregateMap.set(productName, {
          quantity: 0,
          amount: 0,
          target: 0,
          companyIds: new Set<string>(),
          cityIds: new Set<string>(),
          units: new Set<string>(),
        });
      }
      const entry = aggregateMap.get(productName)!;
      entry.quantity += quantity;
      entry.amount += amount;
      entry.target += target;
      if (unit) {
        entry.units.add(unit);
      }
      if (companyKey) {
        entry.companyIds.add(companyKey);
      }
      if (cityKey) {
        entry.cityIds.add(cityKey);
      }
    });
  });

  return Array.from(aggregateMap.entries())
    .map(([productName, entry]) => ({
      productName,
      totalQuantity: entry.quantity,
      totalAmount: entry.amount,
      target: entry.target,
      companyCount: entry.companyIds.size,
      averageRate: entry.quantity > 0 ? entry.amount / entry.quantity : 0,
      cityCount: entry.cityIds.size,
      unit: resolveUnitLabel(entry.units),
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) {
        return b.totalAmount - a.totalAmount;
      }
      if (b.totalQuantity !== a.totalQuantity) {
        return b.totalQuantity - a.totalQuantity;
      }
      return a.productName.localeCompare(b.productName);
    });
};

export const buildProductAggregatesWithFixedTarget = (
  locations: ProductCompanyLocation[],
): ProductAggregate[] => {
  const aggregateMap = new Map<string, ProductAggregateInternal>();

  locations.forEach((location) => {
    const companyKey = computeCompanyKey(location);
    const cityKey = buildCityKey(location.city, location.state, location.country);
    location.products.forEach((product) => {
      const productName = fallbackProductName(product.product_name);
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      const amount = quantity * rate;
      const unit = sanitizeUnit(product.unit);
      const target = safeNumber(product.target);

      if (!aggregateMap.has(productName)) {
        aggregateMap.set(productName, {
          quantity: 0,
          amount: 0,
          target: 0,
          companyIds: new Set<string>(),
          cityIds: new Set<string>(),
          units: new Set<string>(),
        });
      }
      const entry = aggregateMap.get(productName)!;
      entry.quantity += quantity;
      entry.amount += amount;
      if (entry.target === 0 && target > 0) {
        entry.target = target;
      }
      if (unit) {
        entry.units.add(unit);
      }
      if (companyKey) {
        entry.companyIds.add(companyKey);
      }
      if (cityKey) {
        entry.cityIds.add(cityKey);
      }
    });
  });

  return Array.from(aggregateMap.entries())
    .map(([productName, entry]) => ({
      productName,
      totalQuantity: entry.quantity,
      totalAmount: entry.amount,
      target: entry.target,
      companyCount: entry.companyIds.size,
      averageRate: entry.quantity > 0 ? entry.amount / entry.quantity : 0,
      cityCount: entry.cityIds.size,
      unit: resolveUnitLabel(entry.units),
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) {
        return b.totalAmount - a.totalAmount;
      }
      if (b.totalQuantity !== a.totalQuantity) {
        return b.totalQuantity - a.totalQuantity;
      }
      return a.productName.localeCompare(b.productName);
    });
};

type CityProductAggregate = {
  city: string;
  state: string;
  country: string;
  products: Map<string, CityProductInternal>;
  companies: Map<string, CityCompanyInternal>;
};

export const buildCityAggregates = (locations: ProductCompanyLocation[]): Map<string, CityProductAggregate> => {
  const cityMap = new Map<string, CityProductAggregate>();

  locations.forEach((location) => {
    const cityKey = buildCityKey(location.city, location.state, location.country);
    if (!cityMap.has(cityKey)) {
      cityMap.set(cityKey, {
        city: location.city || '',
        state: location.state || '',
        country: location.country || '',
        products: new Map<string, CityProductInternal>(),
        companies: new Map<string, CityCompanyInternal>(),
      });
    }
    const cityEntry = cityMap.get(cityKey)!;
    const companyKey = computeCompanyKey(location);

    location.products.forEach((product) => {
      const productName = fallbackProductName(product.product_name);
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      const amount = quantity * rate;
      const unit = sanitizeUnit(product.unit);

      if (!cityEntry.products.has(productName)) {
        cityEntry.products.set(productName, {
          quantity: 0,
          amount: 0,
          companyIds: new Set<string>(),
          units: new Set<string>(),
        });
      }
      const aggregate = cityEntry.products.get(productName)!;
      aggregate.quantity += quantity;
      aggregate.amount += amount;
      if (unit) {
        aggregate.units.add(unit);
      }
      if (companyKey) {
        aggregate.companyIds.add(companyKey);
      }
    });

    if (!cityEntry.companies.has(companyKey)) {
      cityEntry.companies.set(companyKey, {
        companyKey,
        company: location.company || 'Company',
        userid: location.userid ? String(location.userid) : undefined,
        totalQuantity: 0,
        totalAmount: 0,
        productNames: new Set<string>(),
      });
    }
    const companyAggregate = cityEntry.companies.get(companyKey)!;
    location.products.forEach((product) => {
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      const amount = quantity * rate;
      companyAggregate.totalQuantity += quantity;
      companyAggregate.totalAmount += amount;
      companyAggregate.productNames.add(fallbackProductName(product.product_name));
    });
  });

  return cityMap;
};

export const convertCityAggregatesToInsights = (
  cityMap: Map<string, CityProductAggregate>,
): CityProductInsight[] => {
  const rows: CityProductInsight[] = [];

  cityMap.forEach((entry, cityKey) => {
    const productRows: CityProductRow[] = Array.from(entry.products.entries())
      .map(([productName, aggregate]) => ({
        productName,
        totalQuantity: aggregate.quantity,
        totalAmount: aggregate.amount,
        companyCount: aggregate.companyIds.size,
        averageRate: aggregate.quantity > 0 ? aggregate.amount / aggregate.quantity : 0,
        unit: resolveUnitLabel(aggregate.units),
      }))
      .sort((a, b) => {
        if (b.totalQuantity !== a.totalQuantity) {
          return b.totalQuantity - a.totalQuantity;
        }
        return b.totalAmount - a.totalAmount;
      });

    if (productRows.length === 0) {
      return;
    }

    const companyRows: CityCompanyRow[] = Array.from(entry.companies.values())
      .map((company) => ({
        companyKey: company.companyKey,
        company: company.company,
        userid: company.userid,
        city: entry.city,
        state: entry.state,
        country: entry.country,
        totalQuantity: company.totalQuantity,
        totalAmount: company.totalAmount,
        uniqueProducts: company.productNames.size,
      }))
      .sort((a, b) => {
        if (b.totalQuantity !== a.totalQuantity) {
          return b.totalQuantity - a.totalQuantity;
        }
        return b.totalAmount - a.totalAmount;
      });

    const topProduct = productRows[0];

    rows.push({
      cityKey,
      city: entry.city,
      state: entry.state,
      country: entry.country,
      topProductName: topProduct.productName,
      topProductUnit: topProduct.unit,
      totalQuantity: topProduct.totalQuantity,
      totalAmount: topProduct.totalAmount,
      companyCount: companyRows.length,
      productCount: productRows.length,
      products: productRows,
      companies: companyRows,
    });
  });

  return rows.sort((a, b) => {
    if (b.totalQuantity !== a.totalQuantity) {
      return b.totalQuantity - a.totalQuantity;
    }
    return b.totalAmount - a.totalAmount;
  });
};

export const buildProductCityInsights = (locations: ProductCompanyLocation[]): CityProductInsight[] => {
  const aggregates = buildCityAggregates(locations);
  return convertCityAggregatesToInsights(aggregates);
};

export interface ProductCompanyBreakdownRow {
  companyKey?: string;
  company: string;
  userid?: string;
  city: string;
  state: string;
  country: string;
  totalQuantity: number;
  totalAmount: number;
  unit?: string;
  averageRate: number;
}

export interface CompanyProductRow {
  productName: string;
  totalQuantity: number;
  totalAmount: number;
  averageRate: number;
  unit?: string;
}

interface CompanyProductsDrawerContext {
  company: string;
  city?: string;
  state?: string;
  country?: string;
  rows: ProductInsightRow[];
}

interface CompanySummaryRow {
  companyKey: string;
  company: string;
  userid?: string;
  city: string;
  state: string;
  country: string;
  totalQuantity: number;
  totalAmount: number;
  uniqueProducts: number;
}

interface CompanySelectionRow {
  companyKey?: string;
  company: string;
  userid?: string;
  city: string;
  state: string;
  country: string;
  uniqueProducts: number;
  totalQuantity: number;
  totalAmount: number;
}

interface CompaniesSelectionDrawerContext {
  title: string;
  rows: CompanySelectionRow[];
  locations: ProductCompanyLocation[];
}

export const buildProductCompanyBreakdown = (
  productName: string,
  locations: ProductCompanyLocation[],
): ProductCompanyBreakdownRow[] => {
  const normalizedTarget = fallbackProductName(productName);
  const map = new Map<string, {
    companyKey?: string;
    company: string;
    userid?: string;
    city: string;
    state: string;
    country: string;
    totalQuantity: number;
    totalAmount: number;
    units: Set<string>;
  }>();

  locations.forEach((location) => {
    const companyKey = computeCompanyKey(location);
    const matches = location.products.filter(
      (product) => fallbackProductName(product.product_name) === normalizedTarget,
    );
    if (matches.length === 0) return;

    const mapKey = companyKey || `${location.company}-${location.city}-${location.state}-${location.country}`;
    if (!map.has(mapKey)) {
      map.set(mapKey, {
        companyKey: companyKey || mapKey,
        company: location.company || 'Company',
        userid: location.userid ? String(location.userid) : undefined,
        city: location.city,
        state: location.state,
        country: location.country,
        totalQuantity: 0,
        totalAmount: 0,
        units: new Set<string>(),
      });
    }
    const entry = map.get(mapKey)!;
    matches.forEach((product) => {
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      const unit = sanitizeUnit(product.unit);
      entry.totalQuantity += quantity;
      entry.totalAmount += quantity * rate;
      if (unit) {
        entry.units.add(unit);
      }
    });
  });

  return Array.from(map.values())
    .map((entry) => ({
      companyKey: entry.companyKey,
      company: entry.company,
      userid: entry.userid,
      city: entry.city,
      state: entry.state,
      country: entry.country,
      totalQuantity: entry.totalQuantity,
      totalAmount: entry.totalAmount,
      unit: resolveUnitLabel(entry.units),
      averageRate: entry.totalQuantity > 0 ? entry.totalAmount / entry.totalQuantity : 0,
    }))
    .sort((a, b) => {
      if (b.totalQuantity !== a.totalQuantity) {
        return b.totalQuantity - a.totalQuantity;
      }
      return b.totalAmount - a.totalAmount;
    });
};

export const buildProductCityBreakdown = (
  productName: string,
  cityAggregates: Map<string, CityProductAggregate>,
): ProductCityRow[] => {
  const normalized = fallbackProductName(productName);
  const rows: ProductCityRow[] = [];

  cityAggregates.forEach((aggregate) => {
    const product = aggregate.products.get(normalized);
    if (!product) return;
    rows.push({
      city: aggregate.city,
      state: aggregate.state,
      country: aggregate.country,
      totalQuantity: product.quantity,
      totalAmount: product.amount,
      companyCount: product.companyIds.size,
      unit: resolveUnitLabel(product.units),
    });
  });

  return rows.sort((a, b) => {
    if (b.totalQuantity !== a.totalQuantity) {
      return b.totalQuantity - a.totalQuantity;
    }
    return b.totalAmount - a.totalAmount;
  });
};

interface ProductSummary {
  totalCompanies: number;
  uniqueProducts: number;
  totalQuantity: number;
  totalAmount: number;
}

export interface ProductMarkerBucket {
  key: string;
  color: string;
  label: string;
  min: number;
  max: number;
  gradient?: string;
  shadow?: string;
}

export interface ProductLegendBucketSummary {
  bucket: ProductMarkerBucket;
  companyCount: number;
}

const computeCompanySummaries = (locations: ProductCompanyLocation[]): CompanySummaryRow[] => {
  const map = new Map<string, {
    company: string;
    userid?: string;
    city: string;
    state: string;
    country: string;
    productNames: Set<string>;
    totalQuantity: number;
    totalAmount: number;
  }>();

  locations.forEach((location) => {
    const key = computeCompanyKey(location);
    if (!map.has(key)) {
      map.set(key, {
        company: location.company || 'Company',
        userid: location.userid ? String(location.userid) : undefined,
        city: location.city,
        state: location.state,
        country: location.country,
        productNames: new Set<string>(),
        totalQuantity: 0,
        totalAmount: 0,
      });
    }
    const entry = map.get(key)!;
    location.products.forEach((product) => {
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      entry.totalQuantity += quantity;
      entry.totalAmount += quantity * rate;
      entry.productNames.add(fallbackProductName(product.product_name));
    });
  });

  return Array.from(map.entries())
    .map(([companyKey, entry]) => ({
      companyKey,
      company: entry.company,
      userid: entry.userid,
      city: entry.city,
      state: entry.state,
      country: entry.country,
      totalQuantity: entry.totalQuantity,
      totalAmount: entry.totalAmount,
      uniqueProducts: entry.productNames.size,
    }))
    .sort((a, b) => {
      if (b.totalAmount !== a.totalAmount) {
        return b.totalAmount - a.totalAmount;
      }
      return b.totalQuantity - a.totalQuantity;
    });
};

const buildCompanyProductInsights = (
  companyKey: string,
  locations: ProductCompanyLocation[],
  targetYear?: string,
): ProductInsightRow[] => {
  const productMap = new Map<string, {
    quantity: number;
    amount: number;
    target: number;
    units: Set<string>;
    cityIds: Set<string>;
  }>();

  locations.forEach((location) => {
    if (computeCompanyKey(location) !== companyKey) {
      return;
    }

    const cityKey = buildCityKey(location.city, location.state, location.country);
    location.products.forEach((product) => {
      const productName = fallbackProductName(product.product_name);
      const quantity = safeNumber(product.quantity);
      const rate = safeNumber(product.rate);
      const amount = quantity * rate;
      const unit = sanitizeUnit(product.unit);
      const target = safeNumber(product.target);

      if (!productMap.has(productName)) {
        productMap.set(productName, {
          quantity: 0,
          amount: 0,
          target: 0,
          units: new Set<string>(),
          cityIds: new Set<string>(),
        });
      }
      const entry = productMap.get(productName)!;
      entry.quantity += quantity;
      entry.amount += amount;
      if (entry.target === 0 && target > 0) {
        entry.target = target;
      }
      if (unit) {
        entry.units.add(unit);
      }
      if (cityKey) {
        entry.cityIds.add(cityKey);
      }
    });
  });

  return Array.from(productMap.entries())
    .map(([productName, entry]) => ({
      productName,
      totalQuantity: entry.quantity,
      totalAmount: entry.amount,
      target: entry.target,
      targetYear,
      companyCount: 1,
      cityCount: entry.cityIds.size,
      averageRate: entry.quantity > 0 ? entry.amount / entry.quantity : 0,
      unit: resolveUnitLabel(entry.units),
    }))
    .sort((a, b) => {
      if (b.totalQuantity !== a.totalQuantity) {
        return b.totalQuantity - a.totalQuantity;
      }
      return b.totalAmount - a.totalAmount;
    });
};

const SummaryCard: React.FC<{ 
  label: string; 
  value: string; 
  title?: string;
  colorScheme?: 'blue' | 'purple' | 'amber' | 'emerald' | 'slate';
}> = ({ label, value, title, colorScheme = 'slate' }) => {
  const colorClasses = {
    blue: 'border-blue-200/80 bg-blue-50/90 text-blue-600 [&>span:last-child]:text-blue-900',
    purple: 'border-purple-200/80 bg-purple-50/90 text-purple-600 [&>span:last-child]:text-purple-900',
    amber: 'border-amber-200/80 bg-amber-50/90 text-amber-600 [&>span:last-child]:text-amber-900',
    emerald: 'border-emerald-200/80 bg-emerald-50/90 text-emerald-600 [&>span:last-child]:text-emerald-900',
    slate: 'border-slate-200/80 bg-white/90 text-slate-500 [&>span:last-child]:text-slate-900',
  };

  return (
    <div
      className={`flex min-w-[160px] flex-1 flex-col gap-2 rounded-lg p-4 shadow-sm ${colorClasses[colorScheme]}`}
      title={title}
    >
      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      <span className="text-lg font-semibold" title={title}>
        {value}
      </span>
    </div>
  );
};

interface ProductDrawerExtrasProps {
  summary: ProductSummary;
  topProducts: ProductAggregate[];
  cityInsights: CityProductInsight[];
  onOpenTopProducts: () => void;
  onOpenCityInsights: () => void;
  isLoading: boolean;
}

export const ProductDrawerExtras: React.FC<ProductDrawerExtrasProps> = ({
  summary,
  topProducts,
  cityInsights,
  onOpenTopProducts,
  onOpenCityInsights,
  isLoading,
}) => {
  const { totalCompanies, uniqueProducts, totalQuantity, totalAmount } = summary;
  return (
    <div className="mb-6 flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-6 shadow-xl">
      <h3 className="text-base font-semibold text-slate-800">Product Map Insights</h3>
      <div className="flex flex-wrap gap-3">
        <SummaryCard label="Total Companies" value={formatNumber(totalCompanies)} />
        <SummaryCard label="Unique Products" value={formatNumber(uniqueProducts)} />
        <SummaryCard
          label="Total Quantity"
          value={formatNumber(totalQuantity)}
          title={formatExactNumber(totalQuantity)}
        />
        <SummaryCard
          label="Total Amount"
          value={formatCurrency(totalAmount)}
          title={formatExactCurrency(totalAmount)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <AntButton
          type="primary"
          shape="round"
          onClick={onOpenTopProducts}
          disabled={isLoading || topProducts.length === 0}
        >
          View Top Products
        </AntButton>
        <AntButton
          shape="round"
          onClick={onOpenCityInsights}
          disabled={isLoading || cityInsights.length === 0}
        >
          View City Insights
        </AntButton>
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Spin size="small" />
            <span>Loading product locations...</span>
          </div>
        )}
      </div>
    </div>
  );
};

interface ProductLegendProps {
  summary: ProductSummary;
  bucketSummaries: ProductLegendBucketSummary[];
  isLoading: boolean;
  activeBucketKey: string | null;
  onToggleBucket: (bucketKey: string | null) => void;
}

export const ProductLegend: React.FC<ProductLegendProps> = ({
  summary,
  bucketSummaries,
  isLoading,
  activeBucketKey,
  onToggleBucket,
}) => {
  if (isLoading) {
    return (
      <div className="fixed bottom-24 left-4 z-[1000] flex items-center gap-3 rounded-full border border-slate-200/70 bg-white/90 px-4 py-2 text-sm font-semibold text-slate-700 shadow-2xl backdrop-blur">
        <Spin size="small" />
        <span>Loading product insights...</span>
      </div>
    );
  }

  if (summary.totalCompanies === 0) {
    return (
      <div className="fixed bottom-24 left-4 z-[1000] rounded-full border border-slate-200/70 bg-white/95 px-4 py-2 text-sm font-semibold text-slate-600 shadow-lg">
        No product locations found for the selected filters.
      </div>
    );
  }

  const totalCompaniesLabel = formatNumber(summary.totalCompanies);
  const uniqueProductsLabel = formatNumber(summary.uniqueProducts);
  const isAllActive = !activeBucketKey;
  const getBucketTooltip = (bucketKey: string, min: number, max: number, count: number): string => {
    const countLabel = formatNumber(count);
    const rangeLabel =
      max === Number.POSITIVE_INFINITY ? `${min}+ unique products` : `${min}-${max} unique products`;
    const businessHint =
      bucketKey.includes('bucket-low')
        ? 'Good cross-sell potential.'
        : bucketKey.includes('bucket-high')
          ? 'High-value accounts to protect.'
          : 'Growth accounts to expand.';
    return `${countLabel} companies. Products per company: ${rangeLabel}. ${businessHint}`;
  };
  return (
    <div className="fixed bottom-24 left-4 z-[1000] flex items-center gap-4">
      <div className="flex flex-wrap items-center gap-3 rounded-full border border-slate-200/60 bg-gradient-to-r from-white via-white to-slate-100/80 px-4 py-2 shadow-2xl backdrop-blur">
        <Tooltip title="Total companies in view. Unique products across those companies.">
          <span className="inline-flex">
            <button
              type="button"
              onClick={() => onToggleBucket(null)}
              className={clsx(
                'cursor-pointer flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                isAllActive
                  ? 'border border-slate-900 bg-slate-900 text-white shadow-lg'
                  : 'border border-slate-200/60 bg-white/70 text-slate-900 hover:bg-slate-100',
              )}
            >
              <span>All</span>
              <span className="text-base font-bold">{totalCompaniesLabel}</span>
              <span className={clsx('text-xs font-semibold', isAllActive ? 'text-slate-200' : 'text-slate-500')}>
                ({uniqueProductsLabel} unique)
              </span>
            </button>
          </span>
        </Tooltip>
        <div className="hidden h-6 w-px bg-slate-200/60 sm:block" />
        <div className="flex flex-wrap items-center gap-2">
          {bucketSummaries.map(({ bucket, companyCount }) => {
            const isActive = activeBucketKey === bucket.key;
            const isDisabled = companyCount === 0;
            const handleClick = () => {
              if (isDisabled) return;
              onToggleBucket(bucket.key);
            };

            const tooltip = getBucketTooltip(bucket.key, bucket.min, bucket.max, companyCount);

            return (
              <Tooltip key={bucket.key} title={tooltip}>
                <span className="inline-flex">
                  <button
                    type="button"
                    onClick={handleClick}
                    disabled={isDisabled}
                    className={clsx(
                      'cursor-pointer flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                      isActive
                        ? 'border-transparent text-white shadow-lg'
                        : 'border-slate-200/70 bg-white text-slate-900 hover:bg-slate-100',
                      isDisabled && 'cursor-not-allowed opacity-60',
                    )}
                    style={isActive ? { background: `#0f172b`, boxShadow: `0 12px 30px ${bucket.shadow}` } : undefined}
                  >
                    <span
                      className={clsx('h-2.5 w-2.5 rounded-full border-2', isActive ? 'border-white bg-white/80' : 'bg-transparent')}
                      style={{ borderColor: bucket.color }}
                    />
                    <span className={clsx('font-medium', isActive ? 'text-white' : 'text-slate-800')}>{bucket.label}</span>
                    <span className={clsx('font-bold', isActive ? 'text-white' : 'text-slate-700')}>
                      {formatNumber(companyCount)}
                    </span>
                  </button>
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface ProductMapDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  title: string;
  locations: ProductCompanyLocation[];
  startDate?: string;
  endDate?: string;
}

export const ProductMapDrawer: React.FC<ProductMapDrawerProps> = ({
  open,
  onClose,
  title,
  locations,
  startDate,
  endDate,
}) => {
  const [citySearch, setCitySearch] = useState('');
  const [cityPageSize, setCityPageSize] = useState<number>(10);
  const [cityPage, setCityPage] = useState<number>(1);
  const [cityInsightsMode, setCityInsightsMode] = useState<'city' | 'product' | 'all'>('city');
  const [allProductsLocations, setAllProductsLocations] = useState<ProductCompanyLocation[]>([]);
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [cityProductsDrawerOpen, setCityProductsDrawerOpen] = useState(false);
  const [cityProductsDrawerContext, setCityProductsDrawerContext] = useState<CityProductsDrawerContext | null>(null);
  const [productCitiesDrawerOpen, setProductCitiesDrawerOpen] = useState(false);
  const [productCitiesDrawerContext, setProductCitiesDrawerContext] = useState<ProductCitiesDrawerContext | null>(null);
  const [companyProductsDrawerOpen, setCompanyProductsDrawerOpen] = useState(false);
  const [companyProductsDrawerContext, setCompanyProductsDrawerContext] = useState<CompanyProductsDrawerContext | null>(null);
  const [companiesSelectionDrawerOpen, setCompaniesSelectionDrawerOpen] = useState(false);
  const [companiesSelectionDrawerContext, setCompaniesSelectionDrawerContext] =
    useState<CompaniesSelectionDrawerContext | null>(null);
  const targetYear = useMemo(() => formatYearRange(startDate, endDate), [startDate, endDate]);
  const apiYear = useMemo(() => parseYear(endDate) ?? parseYear(startDate), [startDate, endDate]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let isActive = true;

    const loadAllProducts = async () => {
      setAllProductsLoading(true);
      try {
        const response = await fetchProductCompanyLocations({
          year: apiYear ?? undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        });
        if (isActive) {
          setAllProductsLocations(response.locations ?? []);
        }
      } catch (error) {
        console.error('Failed to load all product locations', error);
        if (isActive) {
          setAllProductsLocations([]);
        }
      } finally {
        if (isActive) {
          setAllProductsLoading(false);
        }
      }
    };

    loadAllProducts();

    return () => {
      isActive = false;
    };
  }, [apiYear, open]);

  // City table column settings state
  const [isCitySettingsModalVisible, setIsCitySettingsModalVisible] = useState(false);
  const [cityColumnVisibility, setCityColumnVisibility] = useState<Record<string, boolean>>({
    city: true,
    state: true,
    country: true,
    productCount: true,
    topProductName: true,
    totalQuantity: true,
    totalAmount: true,
    companyCount: true,
  });
  const [cityColumnOrder, setCityColumnOrder] = useState<Record<string, number>>({});

  // Product Insights table column settings state
  const [isProductSettingsModalVisible, setIsProductSettingsModalVisible] = useState(false);
  const [productColumnVisibility, setProductColumnVisibility] = useState<Record<string, boolean>>({
    productName: true,
    cityCount: true,
    totalQuantity: true,
    unit: true,
    target: true,
    targetYear: true,
    totalAmount: true,
    targetAchievement: true,
    revenueShare: true,
    averageRate: true,
    companyCount: true,
  });
  const [productColumnOrder, setProductColumnOrder] = useState<Record<string, number>>({});

  // Initialize city column order
  useEffect(() => {
    if (Object.keys(cityColumnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        city: 1,
        state: 2,
        country: 3,
        productCount: 4,
        topProductName: 5,
        totalQuantity: 6,
        totalAmount: 7,
        companyCount: 8,
      };
      setCityColumnOrder(defaultOrder);
    }
  }, [cityColumnOrder]);

  const handleCityColumnToggle = (key: string) => {
    if (key === 'city') return; // City is required
    setCityColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetCityColumns = () => {
    const defaultOrder: Record<string, number> = {
      city: 1,
      state: 2,
      country: 3,
      productCount: 4,
      topProductName: 5,
      totalQuantity: 6,
      totalAmount: 7,
      companyCount: 8,
    };
    setCityColumnOrder(defaultOrder);
    setCityColumnVisibility({
      city: true,
      state: true,
      country: true,
      productCount: true,
      topProductName: true,
      totalQuantity: true,
      totalAmount: true,
      companyCount: true,
    });
  };

  const handleReorderCityColumns = (updatedOrder: Record<string, number>) => {
    setCityColumnOrder(updatedOrder);
  };

  const cityColumnDisplayNames: Record<string, string> = {
    city: 'City',
    state: 'State',
    country: 'Country',
    productCount: 'Products',
    topProductName: 'Top Product',
    totalQuantity: 'Top Quantity',
    totalAmount: 'Top Amount',
    companyCount: 'Companies',
  };

  // Initialize product column order
  useEffect(() => {
    if (Object.keys(productColumnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        productName: 1,
        cityCount: 2,
        totalQuantity: 3,
        unit: 4,
        target: 5,
        targetYear: 6,
        totalAmount: 7,
        targetAchievement: 8,
        revenueShare: 9,
        averageRate: 10,
        companyCount: 11,
      };
      setProductColumnOrder(defaultOrder);
    }
  }, [productColumnOrder]);

  const handleProductColumnToggle = (key: string) => {
    if (key === 'productName') return; // Product name is required
    setProductColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetProductColumns = () => {
    const defaultOrder: Record<string, number> = {
      productName: 1,
      cityCount: 2,
      totalQuantity: 3,
      unit: 4,
      target: 5,
      targetYear: 6,
      totalAmount: 7,
      targetAchievement: 8,
      revenueShare: 9,
      averageRate: 10,
      companyCount: 11,
    };
    setProductColumnOrder(defaultOrder);
    setProductColumnVisibility({
      productName: true,
      cityCount: true,
      totalQuantity: true,
      unit: true,
      target: true,
      targetYear: true,
      totalAmount: true,
      targetAchievement: true,
      revenueShare: true,
      averageRate: true,
      companyCount: true,
    });
  };

  const handleReorderProductColumns = (updatedOrder: Record<string, number>) => {
    setProductColumnOrder(updatedOrder);
  };

  const productColumnDisplayNames: Record<string, string> = {
    productName: 'Product',
    cityCount: 'Cities',
    totalQuantity: 'Total Quantity',
    unit: 'Unit',
    target: 'Target',
    targetYear: 'Target Year',
    totalAmount: 'Total Amount',
    targetAchievement: 'Target %',
    revenueShare: 'Revenue %',
    averageRate: 'Avg Rate',
    companyCount: 'Companies',
  };

  useEffect(() => {
    if (!open) {
      setCitySearch('');
      setCityPage(1);
      setCityPageSize(10);
      setCityInsightsMode('city');
      setCityProductsDrawerOpen(false);
      setCityProductsDrawerContext(null);
      setProductCitiesDrawerOpen(false);
      setProductCitiesDrawerContext(null);
      setCompanyProductsDrawerOpen(false);
      setCompanyProductsDrawerContext(null);
      setCompaniesSelectionDrawerOpen(false);
      setCompaniesSelectionDrawerContext(null);
    }
  }, [open]);

  const aggregates = useMemo(() => buildProductAggregates(locations), [locations]);
  const cityAggregates = useMemo(() => buildCityAggregates(locations), [locations]);
  const cityInsights = useMemo(() => convertCityAggregatesToInsights(cityAggregates), [cityAggregates]);
  const allProductAggregates = useMemo(
    () => buildProductAggregatesWithFixedTarget(allProductsLocations),
    [allProductsLocations],
  );
  const allCityAggregates = useMemo(
    () => buildCityAggregates(allProductsLocations),
    [allProductsLocations],
  );

  const summary: ProductSummary = useMemo(() => {
    const companyIds = new Set<string>();
    locations.forEach((location) => {
      const key = computeCompanyKey(location);
      if (key) {
        companyIds.add(key);
      }
    });

    const totals = aggregates.reduce(
      (acc, item) => ({
        quantity: acc.quantity + item.totalQuantity,
        amount: acc.amount + item.totalAmount,
      }),
      { quantity: 0, amount: 0 },
    );

    return {
      totalCompanies: companyIds.size,
      uniqueProducts: aggregates.length,
      totalQuantity: totals.quantity,
      totalAmount: totals.amount,
    };
  }, [aggregates, locations]);

  const totalTarget = useMemo(
    () => aggregates.reduce((sum, item) => sum + item.target, 0),
    [aggregates],
  );
  const targetAchievement = summary.totalAmount;
  const targetRemaining = Math.max(totalTarget - targetAchievement, 0);
  const targetPercent = totalTarget > 0 ? (targetAchievement / totalTarget) * 100 : 0;
  const targetPercentLabel = Number.isFinite(targetPercent)
    ? `${targetPercent.toFixed(1)}% Achieved`
    : '0.0% Achieved';
  const targetPercentTitle = Number.isFinite(targetPercent)
    ? `${targetPercent.toFixed(2)}% Achieved`
    : '0.00% Achieved';
  const targetYearLabel = targetYear || 'N/A';


  const companySummaries = useMemo<CompanySummaryRow[]>(() => computeCompanySummaries(locations), [locations]);
  const allCompanySummaries = useMemo<CompanySummaryRow[]>(
    () => computeCompanySummaries(allProductsLocations),
    [allProductsLocations],
  );

  const productInsightRows = useMemo<ProductInsightRow[]>(
    () =>
      aggregates.map((item) => ({
        productName: item.productName,
        totalQuantity: item.totalQuantity,
        totalAmount: item.totalAmount,
        target: item.target,
        targetYear: targetYear || undefined,
        companyCount: item.companyCount,
        cityCount: item.cityCount,
        averageRate: item.averageRate,
        unit: item.unit,
      })),
    [aggregates, targetYear],
  );
  const allProductInsightRows = useMemo<ProductInsightRow[]>(
    () =>
      allProductAggregates.map((item) => ({
        productName: item.productName,
        totalQuantity: item.totalQuantity,
        totalAmount: item.totalAmount,
        target: item.target,
        targetYear: targetYear || undefined,
        companyCount: item.companyCount,
        cityCount: item.cityCount,
        averageRate: item.averageRate,
        unit: item.unit,
      })),
    [allProductAggregates, targetYear],
  );

  useEffect(() => {
    setCityPage(1);
  }, [
    citySearch,
    cityPageSize,
    cityInsightsMode,
    cityInsights.length,
    productInsightRows.length,
    allProductInsightRows.length,
  ]);

  const filteredCityViewRows = useMemo(() => {
    if (!citySearch.trim()) {
      return cityInsights;
    }
    const term = citySearch.trim().toLowerCase();
    return cityInsights.filter((insight) => {
      const parts = [
        insight.city,
        insight.state,
        insight.country,
        insight.topProductName,
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());
      return parts.some((value) => value.includes(term));
    });
  }, [cityInsights, citySearch]);

  const filteredProductViewRows = useMemo<ProductInsightRow[]>(() => {
    if (!citySearch.trim()) {
      return productInsightRows;
    }
    const term = citySearch.trim().toLowerCase();
    return productInsightRows.filter((row) =>
      row.productName.toLowerCase().includes(term),
    );
  }, [productInsightRows, citySearch]);
  const filteredAllProductViewRows = useMemo<ProductInsightRow[]>(() => {
    if (!citySearch.trim()) {
      return allProductInsightRows;
    }
    const term = citySearch.trim().toLowerCase();
    return allProductInsightRows.filter((row) =>
      row.productName.toLowerCase().includes(term),
    );
  }, [allProductInsightRows, citySearch]);

  const totalProductRevenue = useMemo(
    () => filteredProductViewRows.reduce((sum, row) => sum + row.totalAmount, 0),
    [filteredProductViewRows],
  );
  const totalAllProductRevenue = useMemo(
    () => filteredAllProductViewRows.reduce((sum, row) => sum + row.totalAmount, 0),
    [filteredAllProductViewRows],
  );

  const activeCityRowCount =
    cityInsightsMode === 'city'
      ? filteredCityViewRows.length
      : cityInsightsMode === 'product'
        ? filteredProductViewRows.length
        : filteredAllProductViewRows.length;
  const cityTotalPages = Math.max(1, Math.ceil(activeCityRowCount / cityPageSize));
  const cityPageSafe = Math.min(cityPage, cityTotalPages);
  const paginatedCityRows = useMemo<(CityProductInsight | ProductInsightRow)[]>(() => {
    const rows =
      cityInsightsMode === 'city'
        ? filteredCityViewRows
        : cityInsightsMode === 'product'
          ? filteredProductViewRows
          : filteredAllProductViewRows;
    const start = (cityPageSafe - 1) * cityPageSize;
    return rows.slice(start, start + cityPageSize);
  }, [
    filteredCityViewRows,
    filteredProductViewRows,
    filteredAllProductViewRows,
    cityInsightsMode,
    cityPageSafe,
    cityPageSize,
  ]);

  const companySummaryMaps = useMemo(() => {
    const keyMap = new Map<string, CompanySummaryRow>();
    const locationMap = new Map<string, CompanySummaryRow>();
    companySummaries.forEach((summary) => {
      keyMap.set(summary.companyKey, summary);
      const locationKey = buildCompanyLocationKey(summary.company, summary.city, summary.state, summary.country);
      if (!locationMap.has(locationKey)) {
        locationMap.set(locationKey, summary);
      }
    });
    return { keyMap, locationMap };
  }, [companySummaries]);
  const allCompanySummaryMaps = useMemo(() => {
    const keyMap = new Map<string, CompanySummaryRow>();
    const locationMap = new Map<string, CompanySummaryRow>();
    allCompanySummaries.forEach((summary) => {
      keyMap.set(summary.companyKey, summary);
      const locationKey = buildCompanyLocationKey(summary.company, summary.city, summary.state, summary.country);
      if (!locationMap.has(locationKey)) {
        locationMap.set(locationKey, summary);
      }
    });
    return { keyMap, locationMap };
  }, [allCompanySummaries]);

  const openCompaniesSelectionDrawer = useCallback(
    (title: string, rows: CompanySelectionRow[], sourceLocations: ProductCompanyLocation[]) => {
      setCompanyProductsDrawerOpen(false);
      setCompanyProductsDrawerContext(null);
      setCityProductsDrawerOpen(false);
      setCityProductsDrawerContext(null);
      setProductCitiesDrawerOpen(false);
      setProductCitiesDrawerContext(null);
      setCompaniesSelectionDrawerContext({ title, rows, locations: sourceLocations });
      setCompaniesSelectionDrawerOpen(true);
    },
    [],
  );

  const handleOpenProductCompanies = useCallback(
    (productName: string) => {
      const breakdownRows = buildProductCompanyBreakdown(productName, locations);
      const { keyMap, locationMap } = companySummaryMaps;
      const rows: CompanySelectionRow[] = breakdownRows.map((row) => {
        const summary =
          (row.companyKey && keyMap.get(row.companyKey)) ||
          (row.userid ? keyMap.get(String(row.userid)) : undefined) ||
          locationMap.get(buildCompanyLocationKey(row.company, row.city, row.state, row.country));
        const resolvedKey =
          row.companyKey || summary?.companyKey || (row.userid ? String(row.userid) : undefined);
        return {
          companyKey: resolvedKey,
          company: row.company,
          userid: row.userid,
          city: row.city,
          state: row.state,
          country: row.country,
          uniqueProducts: summary?.uniqueProducts ?? 0,
          totalQuantity: row.totalQuantity,
          totalAmount: row.totalAmount,
        };
      });
      rows.sort((a, b) => {
        if (b.totalAmount !== a.totalAmount) {
          return b.totalAmount - a.totalAmount;
        }
        return b.totalQuantity - a.totalQuantity;
      });
      openCompaniesSelectionDrawer(`Companies for ${productName || 'selected product'}`, rows, locations);
    },
    [locations, companySummaryMaps, openCompaniesSelectionDrawer],
  );

  const handleOpenAllProductCompanies = useCallback(
    (productName: string) => {
      const breakdownRows = buildProductCompanyBreakdown(productName, allProductsLocations);
      const { keyMap, locationMap } = allCompanySummaryMaps;
      const rows: CompanySelectionRow[] = breakdownRows.map((row) => {
        const summary =
          (row.companyKey && keyMap.get(row.companyKey)) ||
          (row.userid ? keyMap.get(String(row.userid)) : undefined) ||
          locationMap.get(buildCompanyLocationKey(row.company, row.city, row.state, row.country));
        const resolvedKey =
          row.companyKey || summary?.companyKey || (row.userid ? String(row.userid) : undefined);
        return {
          companyKey: resolvedKey,
          company: row.company,
          userid: row.userid,
          city: row.city,
          state: row.state,
          country: row.country,
          uniqueProducts: summary?.uniqueProducts ?? 0,
          totalQuantity: row.totalQuantity,
          totalAmount: row.totalAmount,
        };
      });
      rows.sort((a, b) => {
        if (b.totalAmount !== a.totalAmount) {
          return b.totalAmount - a.totalAmount;
        }
        return b.totalQuantity - a.totalQuantity;
      });
      openCompaniesSelectionDrawer(`Companies for ${productName || 'selected product'}`, rows, allProductsLocations);
    },
    [allProductsLocations, allCompanySummaryMaps, openCompaniesSelectionDrawer],
  );

  const handleOpenCityProducts = useCallback(
    (insight: CityProductInsight) => {
      setCityProductsDrawerContext({
        city: insight.city,
        state: insight.state,
        country: insight.country,
        rows: [...insight.products],
      });
      setCityProductsDrawerOpen(true);
    },
    [],
  );

  const handleOpenCityCompanies = useCallback(
    (insight: CityProductInsight) => {
      const rows: CompanySelectionRow[] = insight.companies.map((company) => ({
        companyKey: company.companyKey,
        company: company.company,
        userid: company.userid,
        city: company.city,
        state: company.state,
        country: company.country,
        uniqueProducts: company.uniqueProducts,
        totalQuantity: company.totalQuantity,
        totalAmount: company.totalAmount,
      }));
      const locationLabel = [insight.city, insight.state, insight.country].filter(Boolean).join(', ') || 'Selected City';
      rows.sort((a, b) => {
        if (b.totalAmount !== a.totalAmount) {
          return b.totalAmount - a.totalAmount;
        }
        return b.totalQuantity - a.totalQuantity;
      });
      openCompaniesSelectionDrawer(`Companies in ${locationLabel}`, rows, locations);
    },
    [locations, openCompaniesSelectionDrawer],
  );

  const handleCloseCityProducts = useCallback(() => {
    setCityProductsDrawerOpen(false);
    setCityProductsDrawerContext(null);
  }, []);

  const handleOpenProductCities = useCallback(
    (productName: string) => {
      const rows = buildProductCityBreakdown(productName, cityAggregates);
      setProductCitiesDrawerContext({ productName, rows, locations });
      setProductCitiesDrawerOpen(true);
    },
    [cityAggregates, locations],
  );

  const handleOpenAllProductCities = useCallback(
    (productName: string) => {
      const rows = buildProductCityBreakdown(productName, allCityAggregates);
      setProductCitiesDrawerContext({ productName, rows, locations: allProductsLocations });
      setProductCitiesDrawerOpen(true);
    },
    [allCityAggregates, allProductsLocations],
  );

  const handleOpenProductCityCompanies = useCallback(
    (row: ProductCityRow, context: ProductCitiesDrawerContext) => {
      if (!context?.productName) {
        return;
      }
      const cityKey = buildCityKey(row.city, row.state, row.country);
      const filteredLocations = context.locations.filter(
        (location) => buildCityKey(location.city, location.state, location.country) === cityKey,
      );
      const breakdownRows = buildProductCompanyBreakdown(context.productName, filteredLocations);
      const summaries = computeCompanySummaries(filteredLocations);
      const keyMap = new Map<string, CompanySummaryRow>();
      const locationMap = new Map<string, CompanySummaryRow>();
      summaries.forEach((summary) => {
        keyMap.set(summary.companyKey, summary);
        const locationKey = buildCompanyLocationKey(summary.company, summary.city, summary.state, summary.country);
        if (!locationMap.has(locationKey)) {
          locationMap.set(locationKey, summary);
        }
      });

      const rowsForDrawer: CompanySelectionRow[] = breakdownRows.map((entry) => {
        const summary =
          (entry.companyKey && keyMap.get(entry.companyKey)) ||
          (entry.userid ? keyMap.get(String(entry.userid)) : undefined) ||
          locationMap.get(buildCompanyLocationKey(entry.company, entry.city, entry.state, entry.country));
        const resolvedKey =
          entry.companyKey || summary?.companyKey || (entry.userid ? String(entry.userid) : undefined);
        return {
          companyKey: resolvedKey,
          company: entry.company,
          userid: entry.userid,
          city: entry.city,
          state: entry.state,
          country: entry.country,
          uniqueProducts: summary?.uniqueProducts ?? 0,
          totalQuantity: entry.totalQuantity,
          totalAmount: entry.totalAmount,
        };
      });

      rowsForDrawer.sort((a, b) => {
        if (b.totalAmount !== a.totalAmount) {
          return b.totalAmount - a.totalAmount;
        }
        return b.totalQuantity - a.totalQuantity;
      });
      const locationLabel = [row.city, row.state, row.country].filter(Boolean).join(', ') || 'Selected City';
      openCompaniesSelectionDrawer(
        `Companies in ${locationLabel} for ${context.productName}`,
        rowsForDrawer,
        context.locations,
      );
    },
    [openCompaniesSelectionDrawer],
  );

  const handleCloseProductCities = useCallback(() => {
    setProductCitiesDrawerOpen(false);
    setProductCitiesDrawerContext(null);
  }, []);

  const handleOpenCompanyProductsFromSelection = useCallback(
    (row: CompanySelectionRow) => {
      const sourceLocations = companiesSelectionDrawerContext?.locations ?? locations;
      const summaries = computeCompanySummaries(sourceLocations);
      const keyMap = new Map<string, CompanySummaryRow>();
      const locationMap = new Map<string, CompanySummaryRow>();
      summaries.forEach((summary) => {
        keyMap.set(summary.companyKey, summary);
        const locationKey = buildCompanyLocationKey(summary.company, summary.city, summary.state, summary.country);
        if (!locationMap.has(locationKey)) {
          locationMap.set(locationKey, summary);
        }
      });
      const summary =
        (row.companyKey && keyMap.get(row.companyKey)) ||
        (row.userid ? keyMap.get(String(row.userid)) : undefined) ||
        locationMap.get(buildCompanyLocationKey(row.company, row.city, row.state, row.country));

      const resolvedKey =
        summary?.companyKey ||
        row.companyKey ||
        (row.userid ? String(row.userid) : buildCompanyLocationKey(row.company, row.city, row.state, row.country));

      const resolvedSummary: CompanySummaryRow = summary || {
        companyKey: resolvedKey,
        company: row.company,
        userid: row.userid,
        city: row.city,
        state: row.state,
        country: row.country,
        totalQuantity: row.totalQuantity,
        totalAmount: row.totalAmount,
        uniqueProducts: row.uniqueProducts,
      };

      setCompaniesSelectionDrawerOpen(false);
      setCompaniesSelectionDrawerContext(null);
      const rows = buildCompanyProductInsights(resolvedSummary.companyKey, sourceLocations, targetYear || undefined);
      setCompanyProductsDrawerContext({
        company: resolvedSummary.company,
        city: resolvedSummary.city,
        state: resolvedSummary.state,
        country: resolvedSummary.country,
        rows,
      });
      setCompanyProductsDrawerOpen(true);
    },
    [companiesSelectionDrawerContext, locations, targetYear],
  );

  const handleCloseCompanyProducts = useCallback(() => {
    setCompanyProductsDrawerOpen(false);
    setCompanyProductsDrawerContext(null);
  }, []);

  const handleCloseCompaniesSelection = useCallback(() => {
    setCompaniesSelectionDrawerOpen(false);
    setCompaniesSelectionDrawerContext(null);
  }, []);

  return (
    <Drawer
      title={title}
      placement="right"
      width={DEFAULT_DRAWER_WIDTH}
      push={false}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="flex flex-wrap gap-6">
          <SummaryCard 
            label="Total Companies" 
            value={formatNumber(summary.totalCompanies)}
            colorScheme="blue"
          />
          <SummaryCard 
            label="Unique Products" 
            value={formatNumber(summary.uniqueProducts)}
            colorScheme="purple"
          />
          <SummaryCard
            label="Total Quantity"
            value={formatNumber(summary.totalQuantity)}
            title={formatExactNumber(summary.totalQuantity)}
            colorScheme="amber"
          />
          <SummaryCard
            label="Total Amount"
            value={formatCurrency(summary.totalAmount)}
            title={formatExactCurrency(summary.totalAmount)}
            colorScheme="emerald"
          />
        </div>
        <Box>
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">Target Progress</div>
            <div
              className="text-sm font-bold text-emerald-700 bg-emerald-100 px-5 py-1.5 rounded-full"
              title={targetPercentTitle}
            >
              {targetPercentLabel}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex flex-col gap-1.5 bg-emerald-50 p-4 rounded-lg">
              <div className="text-xs text-emerald-700">Total Target</div>
              <div className="text-lg font-bold text-emerald-600" title={formatExactCurrency(totalTarget)}>
                {formatCurrency(totalTarget)}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 bg-blue-50 p-4 rounded-lg">
              <div className="text-xs text-blue-700">Total Achievement</div>
              <div className="text-lg font-bold text-blue-600" title={formatExactCurrency(targetAchievement)}>
                {formatCurrency(targetAchievement)}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 bg-amber-50 p-4 rounded-lg">
              <div className="text-xs text-amber-700">Target Remaining</div>
              <div className="text-lg font-bold text-amber-600" title={formatExactCurrency(targetRemaining)}>
                {formatCurrency(targetRemaining)}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 bg-purple-50 p-4 rounded-lg">
              <div className="text-xs text-purple-700">Target Year</div>
              <div className="text-base font-semibold text-purple-600">{targetYearLabel}</div>
            </div>
          </div>
        </Box>
        <Divider />

        <Box>
          <div className='flex gap-2 mb-4'>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
            {cityInsightsMode === 'city'
              ? 'City Insights (Top Product by Quantity)'
              : cityInsightsMode === 'product'
                ? 'Product Insights by City Coverage'
                : 'All Products Overview'}
            </Typography>
            <Chip
              label={
                cityInsightsMode === 'city'
                  ? `${filteredCityViewRows.length} cities`
                  : cityInsightsMode === 'product'
                    ? `${filteredProductViewRows.length} products`
                    : `${filteredAllProductViewRows.length} products`
              }
              size="small"
              sx={{
                backgroundColor: cityInsightsMode === 'city' ? '#fdf2f8' : '#e0f2fe',
                color: cityInsightsMode === 'city' ? '#be185d' : '#0369a1',
                fontWeight: 600,
              }}
            />
          </div>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Input
              placeholder={
                cityInsightsMode === 'city' ? 'Search cities...' : 'Search products...'
              }
              suffix={<SearchOutlined className="text-lg !text-blue-600" />}
              value={citySearch}
              onChange={(event) => setCitySearch(event.target.value)}
              className="w-full sm:!w-96 !rounded"
              allowClear
            />
            <Tabs
              activeKey={cityInsightsMode}
              onChange={(value) => setCityInsightsMode((value as 'city' | 'product' | 'all') ?? 'city')}
              items={[
                { key: 'city', label: 'By City' },
                { key: 'product', label: 'By Product' },
              ]}
              size="middle"
              className="!w-full"
              tabBarGutter={8}
            />
          </Box>
        </Box>

        {React.useMemo(() => {
          if (cityInsightsMode === 'city') {
            const allColumns: TableColumnsType<CityProductInsight> = [
                {
                  title: 'City',
                  dataIndex: 'city',
                  key: 'city',
                  sorter: (a, b) => (a.city || '').localeCompare(b.city || ''),
                  render: (city: string) => <span className="font-semibold text-slate-900">{city || 'N/A'}</span>,
                },
                {
                  title: 'State',
                  dataIndex: 'state',
                  key: 'state',
                  sorter: (a, b) => (a.state || '').localeCompare(b.state || ''),
                  render: (state: string) => <span className="text-slate-600">{state || 'N/A'}</span>,
                },
                {
                  title: 'Country',
                  dataIndex: 'country',
                  key: 'country',
                  sorter: (a, b) => (a.country || '').localeCompare(b.country || ''),
                  render: (country: string) => <span className="text-slate-600">{country || 'N/A'}</span>,
                },
                {
                  title: 'Products',
                  dataIndex: 'productCount',
                  key: 'productCount',
                  align: 'center',
                  sorter: (a, b) => a.productCount - b.productCount,
                  render: (count: number, record: CityProductInsight) => (
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      onClick={() => handleOpenCityProducts(record)}
                      sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
                    >
                      {formatNumber(count)}
                    </Button>
                  ),
                },
                {
                  title: 'Top Product',
                  dataIndex: 'topProductName',
                  key: 'topProductName',
                  sorter: (a, b) => a.topProductName.localeCompare(b.topProductName),
                  render: (name: string) => <span className="font-semibold text-blue-700">{name}</span>,
                },
                {
                  title: 'Top Quantity',
                  dataIndex: 'totalQuantity',
                  key: 'totalQuantity',
                  sorter: (a, b) => a.totalQuantity - b.totalQuantity,
                  render: (quantity: number, record: CityProductInsight) => (
                    <span 
                      className="font-semibold text-slate-900"
                      title={formatQuantityTooltip(quantity, record.topProductUnit)}
                    >
                      {formatQuantityWithUnit(quantity, record.topProductUnit)}
                    </span>
                  ),
                },
                {
                  title: 'Top Amount',
                  dataIndex: 'totalAmount',
                  key: 'totalAmount',
                  sorter: (a, b) => a.totalAmount - b.totalAmount,
                  render: (amount: number) => (
                    <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
                      {formatCurrency(amount)}
                    </span>
                  ),
                },
                {
                  title: (
                    <div className='flex justify-between'>
                      Companies
                      <Tooltip title="Column Setting">
                        <AntButton
                          type="text"
                          icon={<SettingOutlined />}
                          onClick={() => setIsCitySettingsModalVisible(true)}
                          size="small"
                        />
                      </Tooltip>
                    </div>
                  ),
                  dataIndex: 'companyCount',
                  key: 'companyCount',
                  align: 'center',
                  sorter: (a, b) => a.companyCount - b.companyCount,
                  render: (count: number, record: CityProductInsight) => (
                    <Button
                      type="button"
                      variant="text"
                      size="small"
                      onClick={() => handleOpenCityCompanies(record)}
                      sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
                    >
                      {formatNumber(count)}
                    </Button>
                  ),
                },
              ];

            const filteredColumns = allColumns
              .filter((col) => cityColumnVisibility[col.key as string])
              .sort((a, b) => {
                const orderA = cityColumnOrder[a.key as string] || 999;
                const orderB = cityColumnOrder[b.key as string] || 999;
                return orderA - orderB;
              });

            return (
              <Table
                columns={filteredColumns}
                dataSource={paginatedCityRows as CityProductInsight[]}
                rowKey="cityKey"
                size="small"
                className='border'
                pagination={{
                  current: cityPageSafe,
                  pageSize: cityPageSize,
                  total: filteredCityViewRows.length,
                  onChange: (page, pageSize) => {
                    setCityPage(page);
                    setCityPageSize(pageSize || 10);
                  },
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
                  className: '!px-4'
                }}
                locale={{
                  emptyText: 'No city insights match the current filters.',
                }}
              />
            );
          } else if (cityInsightsMode === 'product') {
            const allProductColumns: TableColumnsType<ProductInsightRow> = [
              {
                title: 'Product',
                dataIndex: 'productName',
                key: 'productName',
                sorter: (a, b) => a.productName.localeCompare(b.productName),
                render: (name: string) => <span className="font-semibold text-slate-900">{name}</span>,
              },
              {
                title: 'Cities',
                dataIndex: 'cityCount',
                key: 'cityCount',
                align: 'center',
                sorter: (a, b) => a.cityCount - b.cityCount,
                render: (count: number, record: ProductInsightRow) => (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={() => handleOpenProductCities(record.productName)}
                    sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
                  >
                    {formatNumber(count)}
                  </Button>
                ),
              },
              {
                title: 'Total Quantity',
                dataIndex: 'totalQuantity',
                key: 'totalQuantity',
                sorter: (a, b) => a.totalQuantity - b.totalQuantity,
                render: (quantity: number) => (
                  <span className="font-semibold text-slate-900" title={formatExactNumber(quantity)}>
                    {formatNumber(quantity)}
                  </span>
                ),
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                sorter: (a, b) => (a.unit || '').localeCompare(b.unit || ''),
                render: (unit: string | undefined) => <span className="text-slate-600">{unit || '-'}</span>,
              },
              {
                title: 'Target',
                dataIndex: 'target',
                key: 'target',
                sorter: (a, b) => a.target - b.target,
                render: (target: number) => (
                  <span
                    className="font-semibold text-slate-900"
                    title={formatExactCurrency(target)}
                  >
                    {formatCurrency(target)}
                  </span>
                ),
              },
              {
                title: 'Target Year',
                dataIndex: 'targetYear',
                key: 'targetYear',
                sorter: (a, b) => (a.targetYear || '').localeCompare(b.targetYear || ''),
                render: (value: string | undefined) => (
                  <span className="text-slate-600">{value || '-'}</span>
                ),
              },
              {
                title: 'Total Amount',
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                sorter: (a, b) => a.totalAmount - b.totalAmount,
                render: (amount: number) => (
                  <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
                    {formatCurrency(amount)}
                  </span>
                ),
              },
              {
                title: 'Target %',
                dataIndex: 'targetAchievement',
                key: 'targetAchievement',
                sorter: (a, b) => {
                  const aValue = a.target > 0 ? (a.totalAmount / a.target) * 100 : 0;
                  const bValue = b.target > 0 ? (b.totalAmount / b.target) * 100 : 0;
                  return aValue - bValue;
                },
                render: (_: unknown, record: ProductInsightRow) => {
                  const achievement = record.target > 0 ? (record.totalAmount / record.target) * 100 : 0;
                  const display = `${achievement.toFixed(2)}%`;
                  return (
                    <Tooltip title={display}>
                      <span>{display}</span>
                    </Tooltip>
                  );
                },
              },
              {
                title: 'Revenue %',
                dataIndex: 'revenueShare',
                key: 'revenueShare',
                sorter: (a, b) => {
                  const aValue = totalProductRevenue > 0 ? (a.totalAmount / totalProductRevenue) * 100 : 0;
                  const bValue = totalProductRevenue > 0 ? (b.totalAmount / totalProductRevenue) * 100 : 0;
                  return aValue - bValue;
                },
                render: (_: unknown, record: ProductInsightRow) => {
                  const share = totalProductRevenue > 0 ? (record.totalAmount / totalProductRevenue) * 100 : 0;
                  if (share <= 0) {
                    return <span className="text-slate-500">-</span>;
                  }
                  const display = `${share.toFixed(2)}%`;
                  return (
                    <Tooltip title={display}>
                      <span>{display}</span>
                    </Tooltip>
                  );
                },
              },
              {
                title: 'Avg Rate',
                dataIndex: 'averageRate',
                key: 'averageRate',
                sorter: (a, b) => a.averageRate - b.averageRate,
                render: (rate: number) => (
                  <span className="font-semibold text-blue-700">{formatCurrency(rate)}</span>
                ),
              },
              {
                title: (
                  <div className='flex justify-between'>
                    Companies
                    <AntButton
                      type="text"
                      icon={<SettingOutlined />}
                      onClick={() => setIsProductSettingsModalVisible(true)}
                      size="small"
                    />
                  </div>
                ),
                dataIndex: 'companyCount',
                key: 'companyCount',
                align: 'center',
                sorter: (a, b) => a.companyCount - b.companyCount,
                render: (count: number, record: ProductInsightRow) => (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={() => handleOpenProductCompanies(record.productName)}
                    sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
                  >
                    {formatNumber(count)}
                  </Button>
                ),
              },
            ];

            const productColumns = allProductColumns
              .filter((col) => productColumnVisibility[col.key as string])
              .sort((a, b) => {
                const orderA = productColumnOrder[a.key as string] || 999;
                const orderB = productColumnOrder[b.key as string] || 999;
                return orderA - orderB;
              });

            return (
              <Table
                columns={productColumns}
                dataSource={paginatedCityRows as ProductInsightRow[]}
                rowKey="productName"
                size="small"
                className='border'
                pagination={{
                  current: cityPageSafe,
                  pageSize: cityPageSize,
                  total: filteredProductViewRows.length,
                  onChange: (page, pageSize) => {
                    setCityPage(page);
                    setCityPageSize(pageSize || 10);
                  },
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
                  className: '!px-4'
                }}
                locale={{
                  emptyText: 'No product insights match the current filters.',
                }}
              />
            );
          } else {
            const allProductColumns: TableColumnsType<ProductInsightRow> = [
              {
                title: 'Product',
                dataIndex: 'productName',
                key: 'productName',
                sorter: (a, b) => a.productName.localeCompare(b.productName),
                render: (name: string) => <span className="font-semibold text-slate-900">{name}</span>,
              },
              {
                title: 'Cities',
                dataIndex: 'cityCount',
                key: 'cityCount',
                align: 'center',
                sorter: (a, b) => a.cityCount - b.cityCount,
                render: (count: number, record: ProductInsightRow) => (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={() => handleOpenAllProductCities(record.productName)}
                    sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
                  >
                    {formatNumber(count)}
                  </Button>
                ),
              },
              {
                title: 'Total Quantity',
                dataIndex: 'totalQuantity',
                key: 'totalQuantity',
                sorter: (a, b) => a.totalQuantity - b.totalQuantity,
                render: (quantity: number) => (
                  <span className="font-semibold text-slate-900" title={formatExactNumber(quantity)}>
                    {formatNumber(quantity)}
                  </span>
                ),
              },
              {
                title: 'Unit',
                dataIndex: 'unit',
                key: 'unit',
                sorter: (a, b) => (a.unit || '').localeCompare(b.unit || ''),
                render: (unit: string | undefined) => <span className="text-slate-600">{unit || '-'}</span>,
              },
              {
                title: 'Target',
                dataIndex: 'target',
                key: 'target',
                sorter: (a, b) => a.target - b.target,
                render: (target: number) => (
                  <span
                    className="font-semibold text-slate-900"
                    title={formatExactCurrency(target)}
                  >
                    {formatCurrency(target)}
                  </span>
                ),
              },
              {
                title: 'Target Year',
                dataIndex: 'targetYear',
                key: 'targetYear',
                sorter: (a, b) => (a.targetYear || '').localeCompare(b.targetYear || ''),
                render: (value: string | undefined) => (
                  <span className="text-slate-600">{value || '-'}</span>
                ),
              },
              {
                title: 'Total Amount',
                dataIndex: 'totalAmount',
                key: 'totalAmount',
                sorter: (a, b) => a.totalAmount - b.totalAmount,
                render: (amount: number) => (
                  <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
                    {formatCurrency(amount)}
                  </span>
                ),
              },
              {
                title: 'Target %',
                dataIndex: 'targetAchievement',
                key: 'targetAchievement',
                sorter: (a, b) => {
                  const aValue = a.target > 0 ? (a.totalAmount / a.target) * 100 : 0;
                  const bValue = b.target > 0 ? (b.totalAmount / b.target) * 100 : 0;
                  return aValue - bValue;
                },
                render: (_: unknown, record: ProductInsightRow) => {
                  const achievement = record.target > 0 ? (record.totalAmount / record.target) * 100 : 0;
                  const display = `${achievement.toFixed(2)}%`;
                  return (
                    <Tooltip title={display}>
                      <span>{display}</span>
                    </Tooltip>
                  );
                },
              },
              {
                title: 'Revenue %',
                dataIndex: 'revenueShare',
                key: 'revenueShare',
                sorter: (a, b) => {
                  const aValue = totalAllProductRevenue > 0 ? (a.totalAmount / totalAllProductRevenue) * 100 : 0;
                  const bValue = totalAllProductRevenue > 0 ? (b.totalAmount / totalAllProductRevenue) * 100 : 0;
                  return aValue - bValue;
                },
                render: (_: unknown, record: ProductInsightRow) => {
                  const share = totalAllProductRevenue > 0 ? (record.totalAmount / totalAllProductRevenue) * 100 : 0;
                  if (share <= 0) {
                    return <span className="text-slate-500">-</span>;
                  }
                  const display = `${share.toFixed(2)}%`;
                  return (
                    <Tooltip title={display}>
                      <span>{display}</span>
                    </Tooltip>
                  );
                },
              },
              {
                title: 'Avg Rate',
                dataIndex: 'averageRate',
                key: 'averageRate',
                sorter: (a, b) => a.averageRate - b.averageRate,
                render: (rate: number) => (
                  <span className="font-semibold text-blue-700">{formatCurrency(rate)}</span>
                ),
              },
              {
                title: (
                  <div className='flex justify-between'>
                    Companies
                    <AntButton
                      type="text"
                      icon={<SettingOutlined />}
                      onClick={() => setIsProductSettingsModalVisible(true)}
                      size="small"
                    />
                  </div>
                ),
                dataIndex: 'companyCount',
                key: 'companyCount',
                align: 'center',
                sorter: (a, b) => a.companyCount - b.companyCount,
                render: (count: number, record: ProductInsightRow) => (
                  <Button
                    type="button"
                    variant="text"
                    size="small"
                    onClick={() => handleOpenAllProductCompanies(record.productName)}
                    sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
                  >
                    {formatNumber(count)}
                  </Button>
                ),
              },
            ];

            const productColumns = allProductColumns
              .filter((col) => productColumnVisibility[col.key as string])
              .sort((a, b) => {
                const orderA = productColumnOrder[a.key as string] || 999;
                const orderB = productColumnOrder[b.key as string] || 999;
                return orderA - orderB;
              });

            return (
              <Table
                columns={productColumns}
                dataSource={paginatedCityRows as ProductInsightRow[]}
                rowKey="productName"
                size="small"
                className='border'
                loading={allProductsLoading}
                pagination={{
                  current: cityPageSafe,
                  pageSize: cityPageSize,
                  total: filteredAllProductViewRows.length,
                  onChange: (page, pageSize) => {
                    setCityPage(page);
                    setCityPageSize(pageSize || 10);
                  },
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
                  className: '!px-4'
                }}
                locale={{
                  emptyText: allProductsLoading
                    ? 'Loading all products...'
                    : 'No products match the current filters.',
                }}
              />
            );
          }
        }, [
          cityInsightsMode,
          cityColumnVisibility,
          cityColumnOrder,
          productColumnVisibility,
          productColumnOrder,
          paginatedCityRows,
          cityPageSafe,
          cityPageSize,
          filteredCityViewRows.length,
          filteredProductViewRows.length,
          filteredAllProductViewRows.length,
          totalProductRevenue,
          totalAllProductRevenue,
          allProductsLoading,
          handleOpenCityProducts,
          handleOpenCityCompanies,
          handleOpenProductCompanies,
          handleOpenProductCities,
          handleOpenAllProductCompanies,
          handleOpenAllProductCities,
        ])}

        <CompanyProductsDrawer
          open={companyProductsDrawerOpen}
          onClose={handleCloseCompanyProducts}
          context={companyProductsDrawerContext}
        />
        <CompaniesSelectionDrawer
          open={companiesSelectionDrawerOpen}
          onClose={handleCloseCompaniesSelection}
          context={companiesSelectionDrawerContext}
          onOpenCompanyProducts={handleOpenCompanyProductsFromSelection}
        />
        <CityProductsDrawer
          open={cityProductsDrawerOpen}
          onClose={handleCloseCityProducts}
          context={cityProductsDrawerContext}
        />
        <ProductCitiesDrawer
          open={productCitiesDrawerOpen}
          onClose={handleCloseProductCities}
          context={productCitiesDrawerContext}
          onOpenCompanies={handleOpenProductCityCompanies}
        />

        {/* City Column Settings Modal */}
        <ColumnSettingsModal
          isVisible={isCitySettingsModalVisible}
          onCancel={() => setIsCitySettingsModalVisible(false)}
          columns={cityColumnVisibility}
          columnOrder={cityColumnOrder}
          onToggle={handleCityColumnToggle}
          onReset={handleResetCityColumns}
          onReorder={handleReorderCityColumns}
          requiredColumns={['city', 'companyCount']}
          columnDisplayNames={cityColumnDisplayNames}
        />

        {/* Product Column Settings Modal */}
        <ColumnSettingsModal
          isVisible={isProductSettingsModalVisible}
          onCancel={() => setIsProductSettingsModalVisible(false)}
          columns={productColumnVisibility}
          columnOrder={productColumnOrder}
          onToggle={handleProductColumnToggle}
          onReset={handleResetProductColumns}
          onReorder={handleReorderProductColumns}
          requiredColumns={['productName', 'companyCount']}
          columnDisplayNames={productColumnDisplayNames}
        />
      </Box>
    </Drawer>
  );
};

interface ProductTopProductsDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  topProducts: ProductAggregate[];
  summary: ProductSummary;
  isLoading: boolean;
}

export const ProductTopProductsDrawer: React.FC<ProductTopProductsDrawerProps> = ({
  open,
  onClose,
  topProducts,
  summary,
  isLoading,
}) => {
  const rankedProducts = useMemo(
    () => topProducts.slice(0, 10).sort((a, b) => b.totalQuantity - a.totalQuantity),
    [topProducts],
  );

  return (
    <Drawer
      title="Top Products by Quantity"
      placement="right"
      width={1200}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Spin size="small" />
          <span>Loading product insights...</span>
        </div>
      ) : rankedProducts.length === 0 ? (
        <div className="p-4 text-sm text-slate-500">No product data available for this view.</div>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <SummaryCard
              label="Total Quantity"
              value={formatNumber(summary.totalQuantity)}
              title={formatExactNumber(summary.totalQuantity)}
            />
            <SummaryCard
              label="Total Amount"
              value={formatCurrency(summary.totalAmount)}
              title={formatExactCurrency(summary.totalAmount)}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '80px 1.4fr 0.9fr 0.5fr 0.9fr 0.6fr',
              gap: 0,
              px: 1,
              py: 0.75,
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              fontSize: 12,
              fontWeight: 700,
              color: '#475569',
            }}
          >
            <Box>Rank</Box>
            <Box>Product</Box>
            <Box>Quantity</Box>
            <Box>Unit</Box>
            <Box>Total Amount</Box>
            <Box>Companies</Box>
          </Box>

          {rankedProducts.map((product, index) => {
            const quantityShare =
              summary.totalQuantity > 0 ? (product.totalQuantity / summary.totalQuantity) * 100 : 0;
            const quantityValue = formatNumber(product.totalQuantity);
            return (
              <Box
                key={`${product.productName}-${index}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1.4fr 0.9fr 0.5fr 0.9fr 0.6fr',
                  alignItems: 'center',
                  px: 1,
                  py: 0.75,
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <Box>
                  <Chip label={`#${index + 1}`} size="small" color={index === 0 ? 'primary' : 'default'} />
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {product.productName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#0f172a', fontWeight: 600 }}
                  title={formatExactNumber(product.totalQuantity)}
                >
                  {quantityValue}
                  {quantityShare > 0 && (
                    <Typography component="span" variant="caption" sx={{ color: '#64748b', ml: 0.5 }}>
                      ({quantityShare.toFixed(1)}%)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  {product.unit || '-'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#334155', fontWeight: 600 }}
                  title={formatExactCurrency(product.totalAmount)}
                >
                  {formatCurrency(product.totalAmount)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {formatNumber(product.companyCount)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Drawer>
  );
};

interface ProductCityInsightsDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  insights: CityProductInsight[];
  isLoading: boolean;
}

export const ProductCityInsightsDrawer: React.FC<ProductCityInsightsDrawerProps> = ({
  open,
  onClose,
  insights,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState<number>(10);
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setPage(1);
      setPageSize(10);
    }
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, insights.length]);

  const filteredInsights = useMemo(() => {
    if (!searchTerm.trim()) {
      return insights;
    }
    const term = searchTerm.trim().toLowerCase();
    return insights.filter((item) => {
      const parts = [
        item.city,
        item.state,
        item.country,
        item.topProductName,
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());
      return parts.some((value) => value.includes(term));
    });
  }, [insights, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredInsights.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedInsights = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredInsights.slice(start, start + pageSize);
  }, [filteredInsights, safePage, pageSize]);

  return (
    <Drawer
      title="City Product Insights"
      placement="right"
      width={1200}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-slate-500">
          <Spin size="small" />
          <span>Loading city insights...</span>
        </div>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <Search
              placeholder="Search by city, state, country or product"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              allowClear
              style={{ maxWidth: 320 }}
            />
            <Select
              value={pageSize}
              onChange={(value) => setPageSize(Number(value))}
              options={PAGE_SIZE_OPTIONS}
              style={{ width: 140 }}
            />
            <Chip
              label={`${filteredInsights.length} city records`}
              size="small"
              sx={{ backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 600 }}
            />
          </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 0.8fr 0.8fr 1fr 0.6fr 0.6fr 0.8fr 0.5fr',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            px: 1.5,
            py: 1,
              fontWeight: 700,
              color: '#475569',
              fontSize: 12,
            }}
          >
            <Box>City</Box>
          <Box>State</Box>
          <Box>Country</Box>
          <Box>Top Product</Box>
          <Box>Quantity</Box>
          <Box>Unit</Box>
          <Box>Total Amount</Box>
          <Box>Companies</Box>
        </Box>

        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            {paginatedInsights.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: '#64748b' }}>No city insights match the current filters.</Box>
            ) : (
            paginatedInsights.map((item, index) => (
              <Box
                key={`${item.city}-${item.state}-${item.country}-${item.topProductName}-${index}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.8fr 0.8fr 1fr 0.6fr 0.6fr 0.8fr 0.5fr',
                  px: 1.5,
                  py: 0.9,
                  borderBottom: '1px solid #f1f5f9',
                  alignItems: 'center',
                }}
                >
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
                    {item.city || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {item.state || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {item.country || 'N/A'}
                  </Typography>
                <Typography variant="body2" sx={{ color: '#1d4ed8', fontWeight: 600 }}>
                  {item.topProductName}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#0f172a', fontWeight: 600 }}
                  title={formatExactNumber(item.totalQuantity)}
                >
                  {formatNumber(item.totalQuantity)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569' }}>
                  {item.topProductUnit || '-'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#334155', fontWeight: 600 }}
                  title={formatExactCurrency(item.totalAmount)}
                >
                  {formatCurrency(item.totalAmount)}
                </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {formatNumber(item.companyCount)}
                  </Typography>
                </Box>
              ))
            )}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination count={totalPages} page={safePage} onChange={(_, value) => setPage(value)} size="small" />
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

interface CompanyProductsDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  context: CompanyProductsDrawerContext | null;
}

const CompanyProductsDrawer: React.FC<CompanyProductsDrawerProps> = ({ open, onClose, context }) => {
  const rows = context?.rows ?? [];
  const companyLabel = context?.company ? `Products for ${context.company}` : 'Company Products';
  const locationLabel = [context?.city, context?.state, context?.country].filter(Boolean).join(', ');
  const [searchTerm, setSearchTerm] = useState('');

  // Company Products table column settings state
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    productName: true,
    cityCount: true,
    totalQuantity: true,
    unit: true,
    target: true,
    targetYear: true,
    totalAmount: true,
    targetAchievement: true,
    revenueShare: true,
    averageRate: true,
    companyCount: true,
  });
  const [columnOrder, setColumnOrder] = useState<Record<string, number>>({});

  // Initialize column order
  useEffect(() => {
    if (Object.keys(columnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        productName: 1,
        cityCount: 2,
        totalQuantity: 3,
        unit: 4,
        target: 5,
        targetYear: 6,
        totalAmount: 7,
        targetAchievement: 8,
        revenueShare: 9,
        averageRate: 10,
        companyCount: 11,
      };
      setColumnOrder(defaultOrder);
    }
  }, [columnOrder]);

  const handleColumnToggle = (key: string) => {
    if (key === 'productName') return; // Product name is required
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetColumns = () => {
    const defaultOrder: Record<string, number> = {
      productName: 1,
      cityCount: 2,
      totalQuantity: 3,
      unit: 4,
      target: 5,
      targetYear: 6,
      totalAmount: 7,
      targetAchievement: 8,
      revenueShare: 9,
      averageRate: 10,
      companyCount: 11,
    };
    setColumnOrder(defaultOrder);
    setColumnVisibility({
      productName: true,
      cityCount: true,
      totalQuantity: true,
      unit: true,
      target: true,
      targetYear: true,
      totalAmount: true,
      targetAchievement: true,
      revenueShare: true,
      averageRate: true,
      companyCount: true,
    });
  };

  const handleReorderColumns = (updatedOrder: Record<string, number>) => {
    setColumnOrder(updatedOrder);
  };

  const columnDisplayNames: Record<string, string> = {
    productName: 'Product',
    cityCount: 'Cities',
    totalQuantity: 'Total Quantity',
    unit: 'Unit',
    target: 'Target',
    targetYear: 'Target Year',
    totalAmount: 'Total Amount',
    targetAchievement: 'Target %',
    revenueShare: 'Revenue %',
    averageRate: 'Avg Rate',
    companyCount: 'Companies',
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => row.productName.toLowerCase().includes(term));
  }, [rows, searchTerm]);

  const totalCompanyRevenue = useMemo(
    () => filteredRows.reduce((sum, row) => sum + row.totalAmount, 0),
    [filteredRows],
  );

  // Memoize columns
  const tableColumns = React.useMemo(() => {
    const allColumns: TableColumnsType<ProductInsightRow> = [
      {
        title: 'Product',
        dataIndex: 'productName',
        key: 'productName',
        sorter: (a, b) => a.productName.localeCompare(b.productName),
        render: (name: string) => <span className="font-semibold text-slate-900">{name}</span>,
      },
      {
        title: 'Cities',
        dataIndex: 'cityCount',
        key: 'cityCount',
        align: 'center',
        sorter: (a, b) => a.cityCount - b.cityCount,
        render: (count: number) => <span className="text-slate-600">{formatNumber(count)}</span>,
      },
      {
        title: 'Total Quantity',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        sorter: (a, b) => a.totalQuantity - b.totalQuantity,
        render: (quantity: number) => (
          <span className="font-semibold text-slate-900" title={formatExactNumber(quantity)}>
            {formatNumber(quantity)}
          </span>
        ),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit',
        sorter: (a, b) => (a.unit || '').localeCompare(b.unit || ''),
        render: (unit: string | undefined) => <span className="text-slate-600">{unit || '-'}</span>,
      },
      {
        title: 'Target',
        dataIndex: 'target',
        key: 'target',
        sorter: (a, b) => a.target - b.target,
        render: (target: number) => (
          <span className="font-semibold text-slate-900" title={formatExactCurrency(target)}>
            {formatCurrency(target)}
          </span>
        ),
      },
      {
        title: 'Target Year',
        dataIndex: 'targetYear',
        key: 'targetYear',
        sorter: (a, b) => (a.targetYear || '').localeCompare(b.targetYear || ''),
        render: (value: string | undefined) => <span className="text-slate-600">{value || '-'}</span>,
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        sorter: (a, b) => a.totalAmount - b.totalAmount,
        render: (amount: number) => (
          <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
            {formatCurrency(amount)}
          </span>
        ),
      },
      {
        title: 'Target %',
        dataIndex: 'targetAchievement',
        key: 'targetAchievement',
        sorter: (a, b) => {
          const aValue = a.target > 0 ? (a.totalAmount / a.target) * 100 : 0;
          const bValue = b.target > 0 ? (b.totalAmount / b.target) * 100 : 0;
          return aValue - bValue;
        },
        render: (_: unknown, record: ProductInsightRow) => {
          const achievement = record.target > 0 ? (record.totalAmount / record.target) * 100 : 0;
          const display = `${achievement.toFixed(2)}%`;
          return (
            <Tooltip title={display}>
              <span>{display}</span>
            </Tooltip>
          );
        },
      },
      {
        title: 'Revenue %',
        dataIndex: 'revenueShare',
        key: 'revenueShare',
        sorter: (a, b) => {
          const aValue = totalCompanyRevenue > 0 ? (a.totalAmount / totalCompanyRevenue) * 100 : 0;
          const bValue = totalCompanyRevenue > 0 ? (b.totalAmount / totalCompanyRevenue) * 100 : 0;
          return aValue - bValue;
        },
        render: (_: unknown, record: ProductInsightRow) => {
          const share = totalCompanyRevenue > 0 ? (record.totalAmount / totalCompanyRevenue) * 100 : 0;
          if (share <= 0) {
            return <span className="text-slate-500">-</span>;
          }
          const display = `${share.toFixed(2)}%`;
          return (
            <Tooltip title={display}>
              <span>{display}</span>
            </Tooltip>
          );
        },
      },
      {
        title: (
          <div className='flex justify-between'>
            Avg Rate
            <Tooltip title="Column Setting">
              <AntButton
                type="text"
                icon={<SettingOutlined />}
                onClick={() => setIsSettingsModalVisible(true)}
                size="small"
              />
            </Tooltip>
          </div>
        ),
        dataIndex: 'averageRate',
        key: 'averageRate',
        sorter: (a, b) => a.averageRate - b.averageRate,
        render: (rate: number) => (
          <span className="font-semibold text-blue-700">{formatCurrency(rate)}</span>
        ),
      },
      {
        title: 'Companies',
        dataIndex: 'companyCount',
        key: 'companyCount',
        align: 'center',
        sorter: (a, b) => a.companyCount - b.companyCount,
        render: (count: number) => <span className="text-slate-600">{formatNumber(count)}</span>,
      },
    ];

    return allColumns
      .filter((col) => columnVisibility[col.key as string])
      .sort((a, b) => {
        const orderA = columnOrder[a.key as string] || 999;
        const orderB = columnOrder[b.key as string] || 999;
        return orderA - orderB;
      });
  }, [columnVisibility, columnOrder, totalCompanyRevenue]);

  return (
    <Drawer title={companyLabel} placement="right" width={1200} open={open} onClose={onClose} destroyOnClose>
      <>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, color: '#64748b' }}>No product data available for this company.</Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {locationLabel && (
              <Typography variant="body2" sx={{ color: '#475569', px: 0.5 }}>
                {locationLabel}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 0.5 }}>
              <Search
                placeholder="Search products"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                allowClear
                style={{ maxWidth: 320 }}
              />
            </Box>
            <Table
              columns={tableColumns}
              dataSource={filteredRows}
              rowKey="productName"
              size="small"
              className='border'
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
                className: '!px-4'
              }}
              locale={{
                emptyText: 'No product data available for this company.',
              }}
            />
          </Box>
        )}

        <ColumnSettingsModal
          isVisible={isSettingsModalVisible}
          onCancel={() => setIsSettingsModalVisible(false)}
          columns={columnVisibility}
          columnOrder={columnOrder}
          onToggle={handleColumnToggle}
          onReset={handleResetColumns}
          onReorder={handleReorderColumns}
          requiredColumns={['productName']}
          columnDisplayNames={columnDisplayNames}
        />
      </>
    </Drawer>
  );
};

interface CompaniesSelectionDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  context: CompaniesSelectionDrawerContext | null;
  onOpenCompanyProducts: (row: CompanySelectionRow) => void;
}

const CompaniesSelectionDrawer: React.FC<CompaniesSelectionDrawerProps> = ({
  open,
  onClose,
  context,
  onOpenCompanyProducts,
}) => {
  const rows = context?.rows ?? [];
  const title = context?.title || 'Companies in Selection';
  const [searchTerm, setSearchTerm] = useState('');

  // Companies Selection table column settings state
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    company: true,
    location: true,
    uniqueProducts: true,
    totalQuantity: true,
    totalAmount: true,
  });
  const [columnOrder, setColumnOrder] = useState<Record<string, number>>({});

  // Initialize column order
  useEffect(() => {
    if (Object.keys(columnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        company: 1,
        location: 2,
        uniqueProducts: 3,
        totalQuantity: 4,
        totalAmount: 5,
      };
      setColumnOrder(defaultOrder);
    }
  }, [columnOrder]);

  const handleColumnToggle = (key: string) => {
    if (key === 'company') return; // Company is required
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetColumns = () => {
    const defaultOrder: Record<string, number> = {
      company: 1,
      location: 2,
      uniqueProducts: 3,
      totalQuantity: 4,
      totalAmount: 5,
    };
    setColumnOrder(defaultOrder);
    setColumnVisibility({
      company: true,
      location: true,
      uniqueProducts: true,
      totalQuantity: true,
      totalAmount: true,
    });
  };

  const handleReorderColumns = (updatedOrder: Record<string, number>) => {
    setColumnOrder(updatedOrder);
  };

  const columnDisplayNames: Record<string, string> = {
    company: 'Company',
    location: 'Location',
    uniqueProducts: 'Unique Products',
    totalQuantity: 'Total Quantity',
    totalAmount: 'Total Amount',
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => {
      const parts = [
        row.company,
        row.city,
        row.state,
        row.country,
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());
      return parts.some((value) => value.includes(term));
    });
  }, [rows, searchTerm]);

  // Memoize columns
  const tableColumns = React.useMemo(() => {
    const allColumns: TableColumnsType<CompanySelectionRow> = [
      {
        title: 'Company',
        dataIndex: 'company',
        key: 'company',
        sorter: (a, b) => a.company.localeCompare(b.company),
        render: (company: string, record: CompanySelectionRow) => (
          <span className="font-semibold text-slate-900">
            {record.userid ? (
              <a
                href="#"
                onClick={(event) => handleEntityLinkClick(event, record.userid!)}
                className="font-semibold text-emerald-700 underline"
              >
                {company}
              </a>
            ) : (
              company
            )}
          </span>
        ),
      },
      {
        title: 'Location',
        key: 'location',
        sorter: (a, b) => {
          const aValue = [a.city, a.state, a.country].filter(Boolean).join(', ');
          const bValue = [b.city, b.state, b.country].filter(Boolean).join(', ');
          return aValue.localeCompare(bValue);
        },
        render: (_: unknown, record: CompanySelectionRow) => {
          const locationParts = [record.city, record.state, record.country].filter(Boolean);
          return (
            <span className="text-slate-600">
              {locationParts.join(', ') || 'Location unavailable'}
            </span>
          );
        },
      },
      {
        title: 'Unique Products',
        dataIndex: 'uniqueProducts',
        key: 'uniqueProducts',
        align: 'center',
        sorter: (a, b) => a.uniqueProducts - b.uniqueProducts,
        render: (count: number, record: CompanySelectionRow) => (
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => onOpenCompanyProducts(record)}
            disabled={count === 0}
            sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
          >
            {formatNumber(count)}
          </Button>
        ),
      },
      {
        title: 'Total Quantity',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        sorter: (a, b) => a.totalQuantity - b.totalQuantity,
        render: (quantity: number) => (
          <span className="font-semibold text-slate-900" title={formatExactNumber(quantity)}>
            {formatNumber(quantity)}
          </span>
        ),
      },
      {
        title: (
          <div className='flex justify-between'>
            Total Amount
            <Tooltip title="Column Setting">
              <AntButton
                type="text"
                icon={<SettingOutlined />}
                onClick={() => setIsSettingsModalVisible(true)}
                size="small"
              />
            </Tooltip>
          </div>
        ),
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        sorter: (a, b) => a.totalAmount - b.totalAmount,
        render: (amount: number) => (
          <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
            {formatCurrency(amount)}
          </span>
        ),
      },
    ];

    return allColumns
      .filter((col) => columnVisibility[col.key as string])
      .sort((a, b) => {
        const orderA = columnOrder[a.key as string] || 999;
        const orderB = columnOrder[b.key as string] || 999;
        return orderA - orderB;
      });
  }, [columnVisibility, columnOrder, onOpenCompanyProducts]);

  return (
    <Drawer title={title} placement="right" width={1200} open={open} onClose={onClose} destroyOnClose>
      <>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, color: '#64748b' }}>No companies found for this selection.</Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 2, pb: 1 }}>
              <Search
                placeholder="Search company or location"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                allowClear
                style={{ maxWidth: 320 }}
              />
            </Box>
            <Table
              columns={tableColumns}
              dataSource={filteredRows}
              rowKey={(record) => `${record.company}-${record.userid || record.companyKey || record.city}`}
              size="small"
              className='border'
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
                className: '!px-4'
              }}
              locale={{
                emptyText: 'No companies found for this selection.',
              }}
            />
          </>
        )}

        <ColumnSettingsModal
          isVisible={isSettingsModalVisible}
          onCancel={() => setIsSettingsModalVisible(false)}
          columns={columnVisibility}
          columnOrder={columnOrder}
          onToggle={handleColumnToggle}
          onReset={handleResetColumns}
          onReorder={handleReorderColumns}
          requiredColumns={['company', 'totalAmount']}
          columnDisplayNames={columnDisplayNames}
        />
      </>
    </Drawer>
  );
};

interface CityProductsDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  context: CityProductsDrawerContext | null;
}

const CityProductsDrawer: React.FC<CityProductsDrawerProps> = ({ open, onClose, context }) => {
  const rows = context?.rows ?? [];
  const locationLabel =
    [context?.city, context?.state, context?.country].filter(Boolean).join(', ') || 'Selected City';

  // City Products table column settings state
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    productName: true,
    totalQuantity: true,
    unit: true,
    totalAmount: true,
    averageRate: true,
    companyCount: true,
  });
  const [columnOrder, setColumnOrder] = useState<Record<string, number>>({});

  // Initialize column order
  useEffect(() => {
    if (Object.keys(columnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        productName: 1,
        totalQuantity: 2,
        unit: 3,
        totalAmount: 4,
        averageRate: 5,
        companyCount: 6,
      };
      setColumnOrder(defaultOrder);
    }
  }, [columnOrder]);

  const handleColumnToggle = (key: string) => {
    if (key === 'productName') return; // Product name is required
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetColumns = () => {
    const defaultOrder: Record<string, number> = {
      productName: 1,
      totalQuantity: 2,
      unit: 3,
      totalAmount: 4,
      averageRate: 5,
      companyCount: 6,
    };
    setColumnOrder(defaultOrder);
    setColumnVisibility({
      productName: true,
      totalQuantity: true,
      unit: true,
      totalAmount: true,
      averageRate: true,
      companyCount: true,
    });
  };

  const handleReorderColumns = (updatedOrder: Record<string, number>) => {
    setColumnOrder(updatedOrder);
  };

  const columnDisplayNames: Record<string, string> = {
    productName: 'Product',
    totalQuantity: 'Total Quantity',
    unit: 'Unit',
    totalAmount: 'Total Amount',
    averageRate: 'Avg Rate',
    companyCount: 'Companies',
  };

  // Memoize columns outside of conditional rendering
  const tableColumns = React.useMemo(() => {
    const allColumns: TableColumnsType<CityProductRow> = [
      {
        title: 'Product',
        dataIndex: 'productName',
        key: 'productName',
        render: (name: string) => <span className="font-semibold text-slate-900">{name}</span>,
      },
      {
        title: 'Total Quantity',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        render: (quantity: number) => (
          <span className="font-semibold text-slate-900" title={formatExactNumber(quantity)}>
            {formatNumber(quantity)}
          </span>
        ),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit',
        render: (unit: string | undefined) => <span className="text-slate-600">{unit || '-'}</span>,
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        render: (amount: number) => (
          <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
            {formatCurrency(amount)}
          </span>
        ),
      },
      {
        title: 'Avg Rate',
        dataIndex: 'averageRate',
        key: 'averageRate',
        render: (rate: number) => (
          <span className="font-semibold text-blue-700">{formatCurrency(rate)}</span>
        ),
      },
      {
        title: (
          <div className='flex justify-between'>
            Companies
            <Tooltip title="Column Setting">
            <AntButton
              type="text"
              icon={<SettingOutlined />}
              onClick={() => setIsSettingsModalVisible(true)}
              size="small"
            />
            </Tooltip>
          </div>
        ),
        dataIndex: 'companyCount',
        key: 'companyCount',
        render: (count: number) => <span className="text-slate-600">{formatNumber(count)}</span>,
      },
    ];

    return allColumns
      .filter((col) => columnVisibility[col.key as string])
      .sort((a, b) => {
        const orderA = columnOrder[a.key as string] || 999;
        const orderB = columnOrder[b.key as string] || 999;
        return orderA - orderB;
      });
  }, [columnVisibility, columnOrder]);

  return (
    <Drawer
      title={`Products in ${locationLabel}`}
      placement="right"
      width={1200}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      <>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, color: '#64748b' }}>No product data available for this city.</Box>
        ) : (
          <Table
            columns={tableColumns}
            dataSource={rows}
            rowKey="productName"
            size="small"
            className='border'
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
              className: '!px-4'
            }}
            locale={{
              emptyText: 'No product data available for this city.',
            }}
          />
        )}

        <ColumnSettingsModal
          isVisible={isSettingsModalVisible}
          onCancel={() => setIsSettingsModalVisible(false)}
          columns={columnVisibility}
          columnOrder={columnOrder}
          onToggle={handleColumnToggle}
          onReset={handleResetColumns}
          onReorder={handleReorderColumns}
          requiredColumns={['productName', 'companyCount']}
          columnDisplayNames={columnDisplayNames}
        />
      </>
    </Drawer>
  );
};

interface ProductCitiesDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  context: ProductCitiesDrawerContext | null;
  onOpenCompanies: (row: ProductCityRow, context: ProductCitiesDrawerContext) => void;
}

const ProductCitiesDrawer: React.FC<ProductCitiesDrawerProps> = ({ open, onClose, context, onOpenCompanies }) => {
  const rows = context?.rows ?? [];
  const title = context?.productName ? `Cities for ${context.productName}` : 'Product Cities';
  const [searchTerm, setSearchTerm] = useState('');

  // Product Cities table column settings state
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
    city: true,
    state: true,
    country: true,
    totalQuantity: true,
    unit: true,
    totalAmount: true,
    companyCount: true,
  });
  const [columnOrder, setColumnOrder] = useState<Record<string, number>>({});

  // Initialize column order
  useEffect(() => {
    if (Object.keys(columnOrder).length === 0) {
      const defaultOrder: Record<string, number> = {
        city: 1,
        state: 2,
        country: 3,
        totalQuantity: 4,
        unit: 5,
        totalAmount: 6,
        companyCount: 7,
      };
      setColumnOrder(defaultOrder);
    }
  }, [columnOrder]);

  const handleColumnToggle = (key: string) => {
    if (key === 'city') return; // City is required
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleResetColumns = () => {
    const defaultOrder: Record<string, number> = {
      city: 1,
      state: 2,
      country: 3,
      totalQuantity: 4,
      unit: 5,
      totalAmount: 6,
      companyCount: 7,
    };
    setColumnOrder(defaultOrder);
    setColumnVisibility({
      city: true,
      state: true,
      country: true,
      totalQuantity: true,
      unit: true,
      totalAmount: true,
      companyCount: true,
    });
  };

  const handleReorderColumns = (updatedOrder: Record<string, number>) => {
    setColumnOrder(updatedOrder);
  };

  const columnDisplayNames: Record<string, string> = {
    city: 'City',
    state: 'State',
    country: 'Country',
    totalQuantity: 'Total Quantity',
    unit: 'Unit',
    totalAmount: 'Total Amount',
    companyCount: 'Companies',
  };

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return rows;
    }
    return rows.filter((row) => {
      const parts = [row.city, row.state, row.country, row.unit]
        .filter((value): value is string => Boolean(value))
        .map((value) => value.toLowerCase());
      return parts.some((value) => value.includes(term));
    });
  }, [rows, searchTerm]);

  // Memoize columns
  const tableColumns = React.useMemo(() => {
    const allColumns: TableColumnsType<ProductCityRow> = [
      {
        title: 'City',
        dataIndex: 'city',
        key: 'city',
        sorter: (a, b) => (a.city || '').localeCompare(b.city || ''),
        render: (city: string) => <span className="font-semibold text-slate-900">{city || 'N/A'}</span>,
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        sorter: (a, b) => (a.state || '').localeCompare(b.state || ''),
        render: (state: string) => <span className="text-slate-600">{state || 'N/A'}</span>,
      },
      {
        title: 'Country',
        dataIndex: 'country',
        key: 'country',
        sorter: (a, b) => (a.country || '').localeCompare(b.country || ''),
        render: (country: string) => <span className="text-slate-600">{country || 'N/A'}</span>,
      },
      {
        title: 'Total Quantity',
        dataIndex: 'totalQuantity',
        key: 'totalQuantity',
        sorter: (a, b) => a.totalQuantity - b.totalQuantity,
        render: (quantity: number) => (
          <span className="font-semibold text-slate-900" title={formatExactNumber(quantity)}>
            {formatNumber(quantity)}
          </span>
        ),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        key: 'unit',
        sorter: (a, b) => (a.unit || '').localeCompare(b.unit || ''),
        render: (unit: string | undefined) => <span className="text-slate-600">{unit || '-'}</span>,
      },
      {
        title: 'Total Amount',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        sorter: (a, b) => a.totalAmount - b.totalAmount,
        render: (amount: number) => (
          <span className="font-bold text-slate-900" title={formatExactCurrency(amount)}>
            {formatCurrency(amount)}
          </span>
        ),
      },
      {
        title: (
          <div className='flex justify-between'>
            Companies
            <AntButton
              type="text"
              icon={<SettingOutlined />}
              onClick={() => setIsSettingsModalVisible(true)}
              size="small"
            />
          </div>
        ),
        dataIndex: 'companyCount',
        key: 'companyCount',
        sorter: (a, b) => a.companyCount - b.companyCount,
        render: (count: number, record: ProductCityRow) => (
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => context && onOpenCompanies(record, context)}
            disabled={count === 0}
            sx={{ textTransform: 'none', minWidth: 0, padding: 0, fontWeight: 600 }}
          >
            {formatNumber(count)}
          </Button>
        ),
      },
    ];

    return allColumns
      .filter((col) => columnVisibility[col.key as string])
      .sort((a, b) => {
        const orderA = columnOrder[a.key as string] || 999;
        const orderB = columnOrder[b.key as string] || 999;
        return orderA - orderB;
      });
  }, [columnVisibility, columnOrder]);

  return (
    <Drawer title={title} placement="right" width={1200} open={open} onClose={onClose} destroyOnClose>
      <>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, color: '#64748b' }}>No city coverage data available for this product.</Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, px: 2, pb: 1 }}>
              <Search
                placeholder="Search city, state, or country"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                allowClear
                style={{ maxWidth: 320 }}
              />
            </Box>
            <Table
              columns={tableColumns}
              dataSource={filteredRows}
              rowKey={(record) => `${record.city}-${record.state}-${record.country}`}
              size="small"
              className='border'
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} items`,
                className: '!px-4'
              }}
              locale={{
                emptyText: 'No city coverage data available for this product.',
              }}
            />
          </>
        )}

        <ColumnSettingsModal
          isVisible={isSettingsModalVisible}
          onCancel={() => setIsSettingsModalVisible(false)}
          columns={columnVisibility}
          columnOrder={columnOrder}
          onToggle={handleColumnToggle}
          onReset={handleResetColumns}
          onReorder={handleReorderColumns}
          requiredColumns={['city', 'companyCount']}
          columnDisplayNames={columnDisplayNames}
        />
      </>
    </Drawer>
  );
};

interface ProductCompaniesDrawerProps extends Pick<DrawerProps, 'open' | 'onClose'> {
  productName: string;
  rows: ProductCompanyBreakdownRow[];
}

export const ProductCompaniesDrawer: React.FC<ProductCompaniesDrawerProps> = ({
  open,
  onClose,
  productName,
  rows,
}) => {
  const totalQuantity = useMemo(
    () => rows.reduce((sum, row) => sum + row.totalQuantity, 0),
    [rows],
  );
  const totalAmount = useMemo(
    () => rows.reduce((sum, row) => sum + row.totalAmount, 0),
    [rows],
  );

  return (
    <Drawer
      title={`Companies selling ${productName || 'selected product'}`}
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {rows.length === 0 ? (
        <Box sx={{ p: 3, color: '#64748b' }}>No company data available for this product.</Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <SummaryCard
              label="Total Quantity"
              value={formatNumber(totalQuantity)}
              title={formatExactNumber(totalQuantity)}
            />
            <SummaryCard
              label="Total Amount"
              value={formatCurrency(totalAmount)}
              title={formatExactCurrency(totalAmount)}
            />
            <SummaryCard label="Companies" value={formatNumber(rows.length)} />
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr 0.6fr 0.5fr 0.7fr 0.7fr',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 1,
              px: 1.5,
              py: 1,
              fontWeight: 700,
              color: '#475569',
              fontSize: 12,
            }}
          >
            <Box>Company</Box>
            <Box>Location</Box>
            <Box>Quantity</Box>
            <Box>Unit</Box>
            <Box>Total Amount</Box>
            <Box>Avg Rate</Box>
          </Box>

          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden' }}>
            {rows.map((row, index) => {
              const location = [row.city, row.state, row.country].filter(Boolean).join(', ');
              return (
                <Box
                  key={`${row.company}-${row.userid || index}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.2fr 1fr 0.6fr 0.5fr 0.7fr 0.7fr',
                    px: 1.5,
                    py: 1,
                    borderBottom: '1px solid #f1f5f9',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#0f172a' }}>
                    {row.userid ? (
                      <a
                        href="#"
                        onClick={(event) => handleEntityLinkClick(event, row.userid!)}
                        style={{ color: '#0f172a', textDecoration: 'underline', fontWeight: 600 }}
                      >
                        {row.company}
                      </a>
                    ) : (
                      row.company
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {location || 'Location unavailable'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600 }}>
                    {formatNumber(row.totalQuantity)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569' }}>
                    {row.unit || '-'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                    {formatCurrency(row.totalAmount)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1d4ed8', fontWeight: 600 }}>
                    {formatCurrency(row.averageRate)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};











